/**
 * Long-form guides — the content layer.
 *
 * Everyone currently ranking for "freelance contract India" (ipleaders,
 * xflowpay, enterslice, genieai) ranks with long-form articles. The site had
 * eight URLs, all product or legal pages, so it was not in that race at all.
 *
 * Each guide is data, not JSX, so the same object drives three things and they
 * cannot drift apart:
 *   1. the React page a visitor reads,
 *   2. the static HTML written at build time for crawlers that never run JS,
 *   3. the Article + FAQPage structured data for that URL.
 *
 * Writing style is deliberate. Answer engines quote a self-contained paragraph
 * that directly answers a question-shaped heading, so every section leads with
 * the answer and explains afterwards — never the reverse. Statutes are named in
 * plain text because that specificity is what makes a passage worth citing.
 *
 * Body, standfirst and FAQ strings may contain inline links written as
 * [anchor text](/guides/some-slug). Both renderers (GuidePage and the build-time
 * prerender) understand this form; structured data gets the plain anchor text.
 */

export type GuideSection = {
  /** Phrase as a question wherever a real person would search it that way. */
  heading: string;
  /** 40–80 words, self-contained, answer first. This is what gets quoted. */
  body: string[];
  /** Ordered steps, rendered as a numbered list after the body. */
  steps?: string[];
  /** Unordered points, rendered as a bulleted list after the body. */
  bullets?: string[];
  /** A comparison table, rendered after the body. Tables are easy for answer engines to lift. */
  table?: { head: string[]; rows: string[][] };
};

/** Byline shown on every guide; keep in sync with the Article schema author in scripts/prerender-meta.mjs. */
export const GUIDE_AUTHOR = {
  name: 'Saiyam Kumar',
  role: 'Founder, Defellix',
  url: 'https://www.linkedin.com/in/saiyam0211',
} as const;

export const GUIDE_CATEGORIES = ['Getting paid', 'Contracts', 'Tax and invoicing', 'Evidence and law'] as const;
export type GuideCategory = (typeof GUIDE_CATEGORIES)[number];

export type Guide = {
  slug: string;
  /** Groups the guide on the /guides index and picks its card illustration. */
  category: GuideCategory;
  /** Contract template id (src/seo/contractTemplates.ts) offered by this guide's call to action. */
  template?: string;
  title: string;
  metaDescription: string;
  h1: string;
  /** One-paragraph answer to the page's core question, placed above the fold. */
  standfirst: string;
  updated: string;
  /** Slugs of guides to surface under this one — keeps the cluster internally linked. */
  related?: string[];
  sections: GuideSection[];
  faq: { q: string; a: string }[];
};

export const GUIDES: Guide[] = [
  {
    slug: 'client-not-paying-freelancer-india',
    category: 'Getting paid',
    title: 'Client Not Paying? Recovery Options in India | Defellix',
    metaDescription:
      'What to do when an Indian client won\'t pay: demand notice, the MSEFC route, summary suits under Order XXXVII and the interest you can claim.',
    h1: 'Client not paying? What an Indian freelancer can actually do',
    standfirst:
      'If a client has not paid, you have three realistic routes: a written demand notice, a reference to the MSEFC if you were Udyam-registered before the contract, or a civil suit — often a summary suit under Order XXXVII of the Code of Civil Procedure, 1908. Which one is open to you depends on facts you fixed months ago, when the contract was made.',
    updated: '2026-10-17',
    related: ['raise-dispute-against-non-paying-client-india', 'legal-notice-unpaid-freelance-work-india', 'client-ghosted-freelancer-india'],
    sections: [
      {
        heading: 'What should I do first when a client does not pay?',
        body: [
          '[Send a written demand notice](/guides/legal-notice-unpaid-freelance-work-india). It records the amount, the due date and a deadline to pay, and it is the document that starts interest running: under Section 3 of the Interest Act, 1978, a court may allow interest on a debt from the date of a written demand.',
          'You do not need a lawyer to send one. No Indian law requires a pre-suit notice to be drafted or signed by an advocate, and none requires a notice at all before an ordinary breach-of-contract claim. It matters because it proves you asked, and because "I was never told" is the first defence a defaulting client reaches for.',
        ],
        steps: [
          "Confirm the payment is actually overdue under your contract or invoice terms.",
          "Collect the agreement, proof of delivery, the client's approval and your invoices.",
          "Send a written demand notice with the amount, the due date and a deadline to pay.",
          "If there is no response, choose a forum: the MSEFC, a summary suit or a Lok Adalat.",
        ],
      },
      {
        heading: 'How much interest can a freelancer claim on a late payment?',
        body: [
          'If your contract states a rate, you can claim that rate as a contractual entitlement. If it is silent, you cannot simply pick a number: Section 3 of the Interest Act, 1978 caps pre-suit interest at the "current rate of interest", defined in Section 2(b) as the highest rate payable on scheduled-bank deposits, and leaves the award to the court.',
          'This is the single cheapest thing to fix. A late-payment clause agreed before work starts converts interest from something a court may allow into something you are owed.',
        ],
      },
      {
        heading: 'What is the MSEFC route and am I eligible?',
        body: [
          '[The Micro and Small Enterprises Facilitation Council](/guides/msefc-msme-samadhaan-freelancers-india) hears delayed-payment references under Section 18 of the MSMED Act, 2006. It is attractive because no court fee is payable, Section 16 provides compound interest at three times the RBI bank rate, and under Section 19 a buyer challenging the resulting award must first deposit 75% of it.',
          'The catch is timing. In Silpi Industries v. Kerala SRTC (2021), the Supreme Court held that a supplier must have been registered under the MSMED Act as on the date of entering into the contract to claim its benefits. Registering after a client stops paying does not make that contract eligible. Aspects of Section 18 remain contested before the courts, but this is the rule councils and courts have been applying, so treat it as the operating assumption.',
          'The practical consequence is simple: [Udyam registration](/guides/udyam-registration-for-freelancers-india) is free and takes about ten minutes, and the right time to do it is before your next contract, not after a dispute.',
        ],
        table: {
          head: ["Route", "When it fits", "Main cost"],
          rows: [
            ["Demand notice", "Always the first step once payment is overdue", "Postage only"],
            ["MSEFC reference", "You held an Udyam registration before the contract", "No court fee"],
            ["Summary suit (Order XXXVII CPC)", "A fixed sum due under a written contract", "Court fee and advocate fees"],
            ["Lok Adalat", "Both sides are open to a settlement", "No fee"],
            ["Ordinary civil suit", "Damages or disputed amounts", "Court fee and advocate fees"],
          ],
        },
      },
      {
        heading: 'Is a legal notice compulsory before going to court?',
        body: [
          'No, not for an ordinary contract claim. The Indian Contract Act, 1872 requires no pre-suit notice between private parties. Notice is mandatory only in specific situations — Section 80 CPC for suits against the government, Section 138 of the Negotiable Instruments Act for a bounced cheque, Section 106 of the Transfer of Property Act for tenancy, Section 8 IBC for insolvency.',
          'One step is mandatory for larger claims. Where the dispute is a commercial dispute of specified value (₹3 lakh and above), Section 12A of the Commercial Courts Act, 2015 requires pre-institution mediation before filing, unless you are seeking urgent interim relief. In Patil Automation v. Rakheja Engineers the Supreme Court held this is mandatory, and a plaint filed without it is liable to be rejected.',
        ],
      },
      {
        heading: 'What evidence do I need to prove the work was delivered?',
        body: [
          'You need to show what was agreed, what you delivered, and that the client accepted it — each with a date. A signed agreement, dated submissions, and the client\'s own approvals carry far more weight than a WhatsApp thread reconstructed months later.',
          'Electronic records are admissible, but they come with a formality: Section 63 of the Bharatiya Sakshya Adhiniyam, 2023 — the provision that replaced Section 65B of the Evidence Act — requires a certificate identifying the record, describing how it was produced, and giving particulars of the computer systems involved. Gather this while the relationship is still working, not after it breaks down. The full order of steps is in [how to raise a dispute against a non-paying client](/guides/raise-dispute-against-non-paying-client-india).',
        ],
      },
      {
        heading: 'How long do I have to bring a claim?',
        body: [
          'Three years, under the Limitation Act, 1963. Which article applies depends on how you frame the claim: Article 18 governs a suit for the price of work done and runs from the date the work was done where no time for payment was fixed, while Article 55 governs compensation for breach of contract and runs from the date of the breach. Either way the period is three years, and it runs from an event in the past — not from when you gave up chasing.',
          'Section 18 of the Act can extend that period: an acknowledgement of liability, in writing and signed by the client before the three years expire, starts a fresh period from the date of the acknowledgement. An email saying "I will clear it next month" is worth keeping for exactly this reason, though whether a given electronic message satisfies the signature requirement is fact-specific.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I send a legal notice to a client without a lawyer in India?',
        a: 'Yes. No Indian law requires a legal notice to be drafted or signed by an advocate. You can write and send one in your own name as a party to your own contract. An advocate\'s letterhead often prompts a faster response, but it is not a condition of validity.',
      },
      {
        q: 'How much does it cost to recover unpaid freelance fees in India?',
        a: 'The MSEFC route carries no court fee, which is its main attraction for small claims. A civil suit attracts court fees calculated on the amount claimed, which vary by state, plus advocate fees. A demand notice sent yourself costs nothing and resolves a large share of cases without escalation.',
      },
      {
        q: 'Does a WhatsApp agreement count as a contract in India?',
        a: 'It can. Under the Indian Contract Act, 1872 an agreement is valid where there is offer, acceptance, lawful consideration, free consent and a lawful object — no particular form is required. The difficulty is evidentiary: proving what was agreed, when, and that neither side edited the record afterwards. See [is a WhatsApp agreement legally valid](/guides/whatsapp-agreement-legally-valid-india).',
      },
      {
        q: "Can I file a police complaint for unpaid freelance fees?",
        a: "Usually not. Non-payment is generally a civil dispute. A criminal complaint for cheating needs dishonest intent at the start of the deal, and police often treat unpaid invoices as civil matters.",
      },
    ],
  },
  {
    slug: 'freelance-contract-india',
    category: 'Contracts',
    title: 'How to Make a Freelance Contract in India (2026) | Defellix',
    metaDescription:
      "How to make a freelance contract in India, step by step: the clauses to include, e-signature validity, stamp duty, GST and TDS, and mistakes to avoid.",
    h1: 'How to make a freelance contract in India',
    standfirst:
      "To make a freelance contract in India, write down the parties, the scope and exclusions, milestones with amounts and dates, a revision limit, who owns the work, a late-payment interest rate and how either side can end the agreement, then have both sides sign, on paper or electronically. It is valid under the Indian Contract Act, 1872 without stamp paper, a notary or a wet-ink signature; the clauses decide whether it protects you when a client stops replying.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'scope-creep-freelance-contract-clause', 'who-owns-freelance-work-copyright-india'],
    sections: [
      {
        heading: 'What is a freelance contract?',
        body: [
          "A freelance contract is a written agreement between a freelancer and a client that records the work to be done, the price, when it is paid, and what happens if either side does not keep its promises. In India it is an ordinary services agreement governed by the Indian Contract Act, 1872.",
          "Under Section 10 of that Act, an agreement is a contract when it is made with free consent, between parties competent to contract, for lawful consideration and a lawful object. No particular form is required, which is why a signed PDF or an electronically signed agreement is as valid as a printed one.",
        ],
      },
      {
        heading: 'How do I make a freelance contract in India, step by step?',
        body: [
          "Follow these seven steps in order. Each one closes a gap that commonly turns into a dispute later.",
        ],
        steps: [
          "Name the parties: full names or business names, addresses and email addresses of you and the client.",
          "Describe the scope as countable deliverables, and list what is excluded.",
          "Split the fee into milestones, each with an amount and a due date, starting with an advance.",
          "Set a revision limit and define what counts as one round of revisions.",
          "State who owns the work and when ownership passes, ideally on full payment.",
          "Add a late-payment interest rate, termination terms, GST and TDS treatment, and the city whose courts have jurisdiction.",
          "Sign it, on paper or electronically, and keep the signed copy with a record of who signed and when.",
        ],
      },
      {
        heading: 'Is a digital freelance contract legally valid in India?',
        body: [
          "Yes. [Section 5 of the Information Technology Act, 2000](/guides/electronic-signature-legal-validity-india) gives an electronic signature the same legal effect as a handwritten one, and Section 10A confirms that a contract formed electronically is not unenforceable merely because it was made that way.",
          "What matters in practice is proof: who signed, when, and that the document has not changed since. An OTP sent to the client's own email, recorded alongside an integrity hash of the agreement, answers all three. A chat agreement can also be a contract, but it is much harder to prove, as explained in [is a WhatsApp agreement legally valid](/guides/whatsapp-agreement-legally-valid-india).",
        ],
      },
      {
        heading: 'Which clauses should a freelance contract in India include?',
        body: [
          "These eight clauses do most of the work. The two most often missing are the revision limit and the late-payment interest rate; sample wording for every clause is in the [freelance contract template](/guides/freelance-contract-template-india).",
        ],
        table: {
          head: ['Clause', 'What to write', 'Why it matters'],
          rows: [
            ['Scope and exclusions', 'Countable deliverables, formats and quantities, plus a list of what is not included', 'Stops extra requests being treated as part of the price'],
            ['Milestones and payment', 'Amounts and due dates for each stage, with an advance on signing', 'Limits how much unpaid work you can do'],
            ['Revision limit', 'The number of rounds included and the rate for more', 'Prevents a fixed price turning into unlimited work'],
            ['Intellectual property', 'Assignment or licence, and that it passes on full payment', 'Copyright stays with the author unless assigned in writing'],
            ['Late-payment interest', 'A rate per annum from the due date', 'Interest not agreed cannot simply be charged later'],
            ['Termination', 'Notice period and payment for work already accepted', 'Decides what you are owed if the project ends early'],
            ['GST and TDS', 'Whether the fee includes GST, and that payment is net of TDS', 'Stops you absorbing tax or arguing over short payment'],
            ['Jurisdiction', 'Indian law and the courts of a named city', 'Gives a clear forum if there is a dispute'],
          ],
        },
      },
      {
        heading: 'Do I need to register the contract or pay stamp duty?',
        body: [
          "Registration is not required for an ordinary services agreement. Stamp duty is a separate question and is governed by the Indian Stamp Act, 1899 together with the relevant state Act, so the rate depends on where the agreement is executed.",
          "It matters at the point of dispute rather than at signature: under Section 35, an insufficiently stamped instrument can be inadmissible in evidence until the duty and any penalty are paid. Check your state's position before you rely on the agreement in proceedings.",
        ],
      },
      {
        heading: 'How should GST and TDS appear in the contract?',
        body: [
          "State whether your fee is inclusive or exclusive of GST (see [GST for freelancers](/guides/gst-for-freelancers-india)). Registration becomes compulsory once aggregate turnover crosses ₹20 lakh in a financial year, or ₹10 lakh in certain special category states, and an ambiguous fee line is how a freelancer ends up absorbing 18%.",
          "Expect an Indian business client to deduct [TDS on professional fees](/guides/tds-on-freelancer-payments-india), generally 10% once payments cross ₹50,000 in a year. Say in the contract that payment is net of any statutory deduction, with the deduction certificate to be provided.",
        ],
      },
      {
        heading: 'What mistakes make a freelance contract useless in a dispute?',
        body: [
          "Most contracts fail on detail, not validity. These are the gaps that come up again and again when a client stops paying.",
        ],
        bullets: [
          "Scope written as a category, such as “website design”, with no quantities or exclusions.",
          "No payment dates, so you cannot show when the amount became overdue.",
          "Unlimited revisions, or no definition of a revision round.",
          "Ownership of the work passing before payment, or not mentioned at all.",
          "No interest rate, so late payment costs the client nothing.",
          "No record of who signed and when, which makes the agreement easy to deny.",
        ],
      },
      {
        heading: 'How can I create and manage a freelance contract online?',
        body: [
          "You can build one on [Defellix](/signup) in a few minutes. Fill in the scope and exclusions, the milestones with amounts and dates, the revision limit, intellectual-property terms and a late-payment interest rate, and Defellix assembles them into a complete agreement grounded in the Indian Contract Act, 1872. Your first contract is free.",
          "Your client opens a single link and signs with a one-time password sent to their email, with no account or app needed. That signature is an electronic signature under Section 5 of the Information Technology Act, 2000, and the signed agreement is binding like any other contract that meets the requirements above.",
          "The contract does not stop at signing. Each milestone you submit, each client approval or revision request, and each payment you record is timestamped in a tamper-evident log, and you can send invoices from the same place. If a client stops paying, that record becomes the evidence for a [demand notice](/guides/legal-notice-unpaid-freelance-work-india) and a Section 63 certificate, so you manage the whole engagement, from signature to final payment, in one place.",
        ],
      },
    ],
    faq: [
      {
        q: 'Can I make a freelance contract online for free in India?',
        a: "Yes. On Defellix your first contract is free: you set the scope, milestones and payment terms, and your client signs by email OTP without creating an account.",
      },
      {
        q: 'Do I need stamp paper for a freelance contract in India?',
        a: "No. A services agreement is valid on plain paper or in electronic form. Stamp duty may still apply under the Indian Stamp Act, 1899 and the relevant state Act, and an unstamped instrument can be inadmissible in evidence until the duty and penalty are paid, so it affects enforcement, not formation.",
      },
      {
        q: 'Can a freelance contract be signed by email or OTP?',
        a: "Yes. Section 5 of the IT Act, 2000 treats an electronic signature as equivalent to a handwritten one. An OTP delivered to the signer's own email or phone is a common method; the evidentiary strength comes from recording who signed, when, and that the document is unchanged since.",
      },
      {
        q: 'Is a WhatsApp message enough instead of a contract?',
        a: "A WhatsApp agreement can be a valid contract, but it is hard to prove what was agreed and rarely includes a revision limit, payment dates or an interest rate. A short signed contract is much safer.",
      },
      {
        q: 'Who should draft the freelance contract, the freelancer or the client?',
        a: "Either can. Drafting it yourself means the scope, payment terms and ownership clauses are written the way you need them, which is why many freelancers keep their own template.",
      },
    ],
  },
  {
    slug: 'electronic-signature-legal-validity-india',
    category: 'Evidence and law',
    title: 'Are E-Signed Contracts Legally Valid in India? | Defellix',
    metaDescription:
      'Electronic signatures are valid in India under the IT Act, 2000. What makes an e-signed contract hold up, and the Section 63 certificate for evidence.',
    h1: 'Are electronically signed contracts legally valid in India?',
    standfirst:
      'Yes. Section 5 of the Information Technology Act, 2000 gives an electronic signature the same legal effect as a handwritten one, and Section 10A confirms a contract is not unenforceable merely because it was formed electronically. The harder question is not validity but proof — how you demonstrate, months later, who signed and that the document has not changed.',
    updated: '2026-10-17',
    related: ['whatsapp-agreement-legally-valid-india', 'freelance-contract-india', 'freelance-contract-template-india'],
    sections: [
      {
        heading: 'What does the law actually say about e-signatures?',
        body: [
          'Section 5 of the IT Act, 2000 provides that where any law requires information to be authenticated by affixing a signature, that requirement is satisfied by an electronic signature affixed in the prescribed manner. Section 10A removes any doubt about contracts formed by electronic means.',
          'The Act does exclude some documents from electronic execution through its First Schedule, read with Section 1(4). The list was narrowed by a Central Government notification in 2022, which removed contracts for the sale or conveyance of immovable property and relaxed the position for certain negotiable instruments and powers of attorney executed in favour of entities regulated by the RBI, SEBI, IRDAI, NHB or PFRDA. Wills and trusts remain outside the Act.',
          'None of this affects a [freelance services agreement](/guides/freelance-contract-template-india), which has never been excluded. If you are dealing with any document type on that list, check the current text of the Schedule rather than an older summary of it.',
        ],
      },
      {
        heading: 'Is an OTP-based signature good enough?',
        body: [
          'For an ordinary commercial agreement, yes. It is also far easier to prove than a [WhatsApp agreement](/guides/whatsapp-agreement-legally-valid-india). What a court weighs is whether the person who signed can be identified and whether the record is reliable. A one-time password sent to the signer\'s own email or phone, logged with a timestamp and the IP it came from, is meaningful evidence of both.',
          'A Digital Signature Certificate issued by a licensed Certifying Authority under the Second Schedule carries a statutory presumption that an OTP does not. That formality is worth the friction for high-value or regulated documents; for day-to-day freelance work it is usually disproportionate.',
        ],
        table: {
          head: ["Signature method", "Legal status in India", "Strength as evidence"],
          rows: [
            ["Wet-ink signature", "Valid", "Depends on proving the signature is genuine"],
            ["OTP or email e-signature", "Valid for ordinary contracts under Section 5, IT Act", "Strong if the OTP, time, IP and document hash are recorded"],
            ["Aadhaar eSign", "Recognised electronic signature under the Second Schedule", "Strong, tied to verified identity"],
            ["Digital Signature Certificate", "Recognised, issued by a licensed Certifying Authority", "Strongest, with a statutory presumption"],
          ],
        },
      },
      {
        heading: 'What is a Section 63 certificate and why do I need one?',
        body: [
          'Electronic records are admissible, but Section 63 of the Bharatiya Sakshya Adhiniyam, 2023 — the provision that replaced Section 65B of the Indian Evidence Act, 1872 — requires a certificate to accompany them. Without it, a court can refuse to look at the record at all.',
          'The certificate must identify the electronic record, describe how it was produced, give particulars of the devices involved, and deal with the conditions in sub-section (2): that the computer was in regular use, that information was regularly fed into it in the ordinary course of activity, and that it was operating properly. Sub-section (4) contemplates it being signed both by the person in charge of the relevant activity and by an expert.',
        ],
        bullets: [
          "Identifies the electronic record, such as the signed agreement file.",
          "Describes how the record was produced and the device or system involved.",
          "Gives the hash value and algorithm used to fingerprint the record.",
          "Confirms the computer was in regular use and operating properly.",
          "Is signed by the person in charge, with Part B signed by an expert.",
        ],
      },
      {
        heading: 'Does a blockchain timestamp add anything legally?',
        body: [
          'It is not a substitute for the Section 63 certificate and no Indian statute mentions it. What it does is make tampering detectable by anyone: once a hash of the agreement is written to a public chain, altering the document afterwards produces a different hash, and the mismatch is verifiable without trusting the platform that stored it.',
          'Practically, that shifts the argument. Instead of asking a court to take your word about a file\'s history, you can point to a record neither party controls.',
        ],
      },
    ],
    faq: [
      {
        q: 'Does an electronic signature need to be from a licensed provider in India?',
        a: 'Not for ordinary contracts. Section 5 of the IT Act, 2000 recognises electronic signatures generally. A Digital Signature Certificate from a licensed Certifying Authority attracts a stronger statutory presumption, which matters for high-value or regulated documents, but is not required for a services agreement.',
      },
      {
        q: 'Which documents cannot be signed electronically in India?',
        a: 'The First Schedule to the IT Act, 2000 excludes negotiable instruments other than cheques, powers of attorney, trusts, wills and testamentary dispositions, and contracts for the sale or conveyance of immovable property. Everything else, including freelance service agreements, may be executed electronically.',
      },
      {
        q: "Is Aadhaar eSign better than an OTP signature?",
        a: "Aadhaar eSign ties the signature to a verified identity, which makes it stronger evidence. For ordinary freelance contracts an OTP signature with a full audit trail is legally valid and usually sufficient.",
      },
      {
        q: "Can I e-sign a contract with a client outside India?",
        a: "Yes, Indian law recognises the e-signature, but which country's law governs the contract and where disputes are heard should be stated in the agreement.",
      },
    ],
  },
  {
    slug: 'raise-dispute-against-non-paying-client-india',
    category: 'Getting paid',
    title: 'Raise a Dispute Against a Non-Paying Client | Defellix',
    metaDescription:
      'Steps to raise a payment dispute against a client in India: evidence, demand notice, then MSEFC, a summary suit or Lok Adalat. Know the order.',
    h1: 'How to raise a dispute against a client who has not paid (India)',
    standfirst:
      'Raising a payment dispute in India is a sequence, not a single filing: confirm the payment is actually overdue, assemble proof of the agreement and the delivery, send a written demand, and only then choose a forum — the MSEFC if you were Udyam-registered before the contract, a summary suit under Order XXXVII of the Code of Civil Procedure, 1908, or a Lok Adalat. Doing the early steps properly decides how strong every later step is.',
    updated: '2026-10-17',
    related: ['legal-notice-unpaid-freelance-work-india', 'msefc-msme-samadhaan-freelancers-india', 'summary-suit-order-37-recover-money-india'],
    sections: [
      {
        heading: 'What are the steps to raise a payment dispute against a client in India?',
        body: [
          'There are five steps, in this order: confirm the amount is overdue under the contract, gather your evidence, [send a written demand notice](/guides/legal-notice-unpaid-freelance-work-india), give the client a short, fixed period to respond, and then file in the forum that fits your facts. Skipping straight to a filing is the most common mistake, because a court or council will ask what you did to resolve it first.',
          'Most disputes over small and mid-sized freelance invoices end at step three or four. A clear, dated demand that sets out the contract, the work delivered and the amount due often produces payment or a settlement without any filing.',
        ],
        steps: [
          "Confirm the amount is overdue under the contract.",
          "Gather your evidence: agreement, scope, delivery, acceptance, invoices and reminders.",
          "Send a written demand notice by email and registered post.",
          "Give the client a fixed period, usually seven to fifteen days, to pay.",
          "File in the forum that fits your facts: MSEFC, summary suit or Lok Adalat.",
        ],
      },
      {
        heading: 'How do I know the payment is actually overdue?',
        body: [
          'A payment is overdue only after the due date in your contract or invoice has passed. Check your agreement for a payment term, a milestone date or an acceptance period. If the contract says nothing, you are relying on the invoice terms and the course of dealing, which is harder to prove.',
          'Do not send a demand before the due date. A demand for a debt that has not yet matured gives you no cause of action and weakens your credibility with whoever reads it later. If a milestone was accepted but is not yet due, wait for the date and then act.',
        ],
      },
      {
        heading: 'What evidence should I collect before I raise a dispute?',
        body: [
          'Collect four things, each with a date: the agreement or written terms, the scope and any approved changes, proof that you delivered the work, and proof that the client accepted it or used it. Add your invoices, the payment schedule and every reminder you have already sent.',
          'Preserve the originals rather than screenshots where you can: the email files with headers, the exported chat, the delivery links and the server or platform timestamps. If you may need to put electronic records before a court, they need a [certificate under Section 63 of the Bharatiya Sakshya Adhiniyam, 2023](/guides/electronic-signature-legal-validity-india), so keeping the source material intact matters more than a tidy summary.',
        ],
      },
      {
        heading: 'Which forum should I choose for an unpaid freelance invoice?',
        body: [
          'Choose by your facts. If you held an [Udyam registration](/guides/udyam-registration-for-freelancers-india) before you signed the contract, [the Micro and Small Enterprises Facilitation Council](/guides/msefc-msme-samadhaan-freelancers-india) under Section 18 of the MSMED Act, 2006 is usually the cheapest route, with no court fee. If you were not, a [summary suit under Order XXXVII CPC](/guides/summary-suit-order-37-recover-money-india) is built for debts arising on a written contract. For a quick, free settlement attempt, a pre-litigation Lok Adalat is available.',
          'The amount also matters. For a commercial dispute of ₹3 lakh or more, Section 12A of the Commercial Courts Act, 2015 requires pre-institution mediation before you file, unless you seek urgent interim relief. Below that, a well-evidenced demand and the MSEFC route are usually proportionate.',
        ],
        table: {
          head: ["Forum", "Choose it when", "Watch out for"],
          rows: [
            ["MSEFC (MSME Samadhaan)", "You were Udyam-registered before the contract", "Eligibility can be challenged; the law is still settling"],
            ["Summary suit", "You have a written contract and a fixed unpaid amount", "Court fees; mediation first if ₹3 lakh or more"],
            ["Lok Adalat", "The client is willing to settle", "Needs both sides to agree"],
            ["Ordinary civil suit", "The amount or liability is disputed", "Slowest and most expensive"],
          ],
        },
      },
      {
        heading: 'Do I need a lawyer to raise a dispute?',
        body: [
          'No, not to send a demand or to file a reference before the MSEFC as a party to your own contract. No Indian statute requires a demand notice to be drafted or signed by an advocate. A lawyer becomes worth the cost when the sum is large, the client contests liability, or you need to file a suit.',
          'If you do use one, bring the organised evidence file described above. An advocate can only work with what you can prove, and a clean timeline saves their time and your money.',
        ],
      },
      {
        heading: 'How long do I have to raise a dispute?',
        body: [
          'Generally three years under the Limitation Act, 1963, counted from the date payment fell due or the breach occurred, not from the date you stopped chasing. Waiting does not pause the clock.',
          'A written acknowledgement of the debt signed by the client within the three years can start a fresh period under Section 18 of the Act, so keep any message in which the client admits the amount is owed.',
        ],
      },
      {
        heading: 'What should I avoid when a client does not pay?',
        body: [
          'Avoid threats you cannot or should not carry out, such as threatening a criminal case for an ordinary unpaid debt, or publishing the client\'s details to shame them. These can expose you to a counter-complaint and give the client a way to change the subject.',
          'Also avoid changing your story between messages, deleting the thread or accepting partial payments without recording what the payment was for. Keep every communication factual, dated and in writing.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I raise a payment dispute online in India?',
        a: 'For MSME suppliers, the MSME Samadhaan portal lets you file a delayed-payment reference to the Facilitation Council online. Civil suits and Lok Adalat applications are filed with the relevant court or legal services authority, which may offer e-filing depending on the state.',
      },
      {
        q: 'What is the first thing to do when a freelance client refuses to pay?',
        a: 'Confirm the due date has passed, collect your agreement and proof of delivery, and send a written demand that states the amount, the contract and a deadline. This creates the paper trail every later forum will ask for.',
      },
      {
        q: 'Is a dispute the same as a legal notice?',
        a: 'No. A [legal or demand notice](/guides/legal-notice-unpaid-freelance-work-india) is a letter you send to the client. A dispute is the wider process, which can include that notice and then a reference to the MSEFC, a Lok Adalat application or a court suit.',
      },
      {
        q: 'Does raising a dispute cost money?',
        a: 'A demand notice you send yourself costs only postage. The MSEFC reference carries no court fee. A civil suit attracts court fees based on the claim amount, plus any advocate fees.',
      },
    ],
  },
  {
    slug: 'legal-notice-unpaid-freelance-work-india',
    category: 'Getting paid',
    title: 'Legal Notice for Unpaid Freelance Work: Sample | Defellix',
    metaDescription:
      'How to write and serve a demand notice for unpaid freelance work in India: what to include, a sample to adapt, and mistakes to avoid.',
    h1: 'Legal notice for unpaid freelance work: format and sample (India)',
    standfirst:
      'A legal notice for unpaid freelance work is a dated, written demand that identifies the contract, the work you delivered, the amount overdue and a deadline to pay. It is not compulsory before an ordinary contract claim in India, but it proves you asked, starts the clock on interest, and is usually the document a forum asks to see first.',
    updated: '2026-10-17',
    related: ['raise-dispute-against-non-paying-client-india', 'client-ghosted-freelancer-india', 'summary-suit-order-37-recover-money-india'],
    sections: [
      {
        heading: 'What is a legal notice for unpaid freelance work?',
        body: [
          'It is a written demand sent by you, as the person owed money, to the client who has not paid. It records what was agreed, what you delivered, what is overdue and what you will do if payment does not arrive. It is a pre-litigation letter, not a court order, and it is not enforceable by itself.',
          'Its value is evidential and practical. It shows you made a clear demand on a date, it gives the client a last chance to pay without a filing, and it supports a claim for interest from the date of the demand.',
        ],
      },
      {
        heading: 'Is a legal notice compulsory before going to court in India?',
        body: [
          'Not for an ordinary breach-of-contract or unpaid-invoice claim. No provision of the Indian Contract Act, 1872 requires a pre-suit notice between private parties. Notice is mandatory only in specific situations, such as a suit against the government under Section 80 CPC or a cheque-bounce complaint under Section 138 of the Negotiable Instruments Act.',
          'Sending one is still strongly advisable. It is cheap, it is the natural first exhibit in any later filing, and a client who has been formally asked is much harder to excuse when they say they never knew.',
        ],
      },
      {
        heading: 'What should a demand notice for unpaid fees include?',
        body: [
          'Include these items: the date; your name and address; the client\'s name and address; the contract or project reference and its date; a short summary of the work delivered and accepted; each invoice number, date and amount; the due date; the total outstanding; the interest you are claiming and its basis; the bank or UPI details to pay into; and a clear deadline, commonly seven to fifteen days.',
          'Close with a neutral statement that you reserve the right to approach the appropriate forum, and sign it. Keep the tone factual. Attach copies of the agreement, the invoices and the proof of delivery, and list them as annexures.',
        ],
        bullets: [
          "Date, your name and address, and the client's name and address.",
          "The contract or project reference and its date.",
          "A short summary of the work delivered and accepted.",
          "Each invoice number, date and amount, and the due date.",
          "The total outstanding and the interest claimed, with its basis.",
          "Bank or UPI details for payment and a clear deadline.",
          "A neutral reservation of your right to approach the appropriate forum, your signature, and a list of annexures.",
        ],
      },
      {
        heading: 'What does a sample legal notice for unpaid freelance work look like?',
        body: [
          'Heading: “Demand Notice for Recovery of Outstanding Professional Fees”. Date: [date]. To: [client name, designation, company, address, email]. From: [your name, address, email, PAN or Udyam number if any].',
          'Body: “Under the agreement dated [date] (Ref. [contract reference]), I agreed to provide [service] for [project]. I delivered [milestone or deliverable] on [date], which you accepted on [date] / have used since [date]. Invoice no. [x] dated [date] for ₹[amount] fell due on [due date] and remains unpaid. The total outstanding is ₹[amount].”',
          'Demand: “I require payment of ₹[amount], together with interest at [rate]% per annum from [due date] until payment, within [7/15] days of receipt of this notice to [account or UPI details]. If payment is not received, I reserve the right to take such steps as are available to me, including approaching the appropriate forum, at your cost.” Sign and list annexures.',
        ],
      },
      {
        heading: 'How much interest can I claim in the notice?',
        body: [
          'If your contract states a late-payment rate, claim that rate. If it does not, do not invent one: Section 3 of the Interest Act, 1978 limits what a court may allow before a suit to the current rate of interest, and awards it at the court\'s discretion.',
          'If you are registered under the [MSMED Act](/guides/msefc-msme-samadhaan-freelancers-india) and the contract qualifies, Section 16 provides compound interest at three times the RBI bank rate, but that is applied by the Facilitation Council and depends on eligibility, so do not state it as certain in a general notice.',
        ],
      },
      {
        heading: 'How do I serve the notice so I can prove it was received?',
        body: [
          'Send it by email to the address in the contract and also by registered post or speed post with acknowledgement due to the postal address. Keep the sent email, the postal receipt and the tracking record. If the client has an official representative or company secretary, address a copy there.',
          'Record the date and method each time. A notice you cannot prove was delivered loses most of its value, so the proof of service matters as much as the letter itself.',
        ],
        steps: [
          "Email the notice to the address in the contract and keep the sent email.",
          "Send the same notice by registered post or speed post with acknowledgement due.",
          "Keep the postal receipt and the tracking record.",
          "Note the date of each delivery, because the deadline runs from receipt.",
        ],
      },
      {
        heading: 'What mistakes make a demand notice weaker?',
        body: [
          'The common ones are sending it before the payment is due (silence alone is not a default, as the guide for a [client who has ghosted you](/guides/client-ghosted-freelancer-india) explains), quoting the wrong amount or a figure that includes work not delivered, threatening criminal action for a civil debt, and missing the contract reference and dates.',
          'A vague notice is easy to answer with a vague reply. Be exact about what was agreed, delivered and unpaid, and let the facts carry the pressure.',
        ],
      },
    ],
    faq: [
      {
        q: 'Can I write and send a legal notice myself without an advocate?',
        a: 'Yes. No Indian law requires a demand notice to be drafted or signed by an advocate. You can send it in your own name as a party to the contract. An advocate\'s letterhead may prompt a quicker response but is not needed for it to be valid.',
      },
      {
        q: 'How many days should I give the client in a legal notice?',
        a: 'Seven to fifteen days is common for a demand of this kind. Choose a period that is reasonable given the amount and the relationship, state it clearly, and count it from the date the client receives the notice.',
      },
      {
        q: 'Do I need to send the notice by registered post?',
        a: 'It is not a legal requirement, but sending it by email and by registered or speed post with acknowledgement gives you stronger proof that it was received.',
      },
      {
        q: 'What if the client ignores the notice?',
        a: 'Your options then include a reference to the [MSEFC](/guides/msefc-msme-samadhaan-freelancers-india) if you qualify, a summary suit under Order XXXVII CPC, or a Lok Adalat application. The unanswered notice becomes part of your evidence, and the [steps to raise a dispute](/guides/raise-dispute-against-non-paying-client-india) explain what to do next.',
      },
    ],
  },
  {
    slug: 'msefc-msme-samadhaan-freelancers-india',
    category: 'Getting paid',
    title: 'MSEFC & MSME Samadhaan for Freelancers | Defellix',
    metaDescription:
      'Can a freelancer use MSEFC and MSME Samadhaan for unpaid fees? Eligibility, the Udyam timing rule, interest and filing steps explained.',
    h1: 'MSEFC and MSME Samadhaan for freelancers: how it works',
    standfirst:
      'A freelancer who is registered on Udyam can ask the Micro and Small Enterprises Facilitation Council to recover delayed payment under Section 18 of the MSMED Act, 2006, usually through the MSME Samadhaan portal. It carries no court fee and provides compound interest at three times the RBI bank rate, but the Supreme Court has tied the benefit to being registered when the contract was made.',
    updated: '2026-10-17',
    related: ['udyam-registration-for-freelancers-india', 'legal-notice-unpaid-freelance-work-india', 'raise-dispute-against-non-paying-client-india'],
    sections: [
      {
        heading: 'What is the MSEFC and what is MSME Samadhaan?',
        body: [
          'The Micro and Small Enterprises Facilitation Council is a state-level body set up under the MSMED Act, 2006 to resolve delayed-payment disputes between micro or small suppliers and their buyers. MSME Samadhaan is the Government of India portal through which such references can be filed and tracked online.',
          'It is meant to be a faster and cheaper route than a civil suit. The Council first attempts conciliation and, if that fails, the dispute goes to arbitration under the Arbitration and Conciliation Act, 1996.',
        ],
      },
      {
        heading: 'Can a freelancer file a case with the MSEFC?',
        body: [
          'Yes, if you are a registered micro or small enterprise supplying services, which includes a sole proprietor freelancer holding an Udyam registration. The Act covers a “supplier” who has filed a memorandum or registered under the Act, and delayed payment for services is within the scheme.',
          'The condition that decides most cases is timing. In Silpi Industries v. Kerala SRTC (2021), the Supreme Court held that the supplier must be registered on the date of entering into the contract. A later decision, NBCC (India) Ltd v. State of West Bengal (2025), disagreed and referred the question to a larger bench, so the position is not fully settled. Plan on the stricter view.',
        ],
      },
      {
        heading: 'What does the MSEFC route give a freelancer over a civil suit?',
        body: [
          'No court fee is payable for the reference. Section 15 requires payment within the agreed time, capped at 45 days from acceptance, and Section 16 provides compound interest with monthly rests at three times the bank rate notified by the RBI. Under Section 19, a buyer who challenges the award must first deposit 75% of the awarded amount.',
          'Together these change the economics of a small claim. A debt that would not justify a suit can still be pursued, and the deposit requirement gives the buyer a reason to settle.',
        ],
        table: {
          head: ["", "MSEFC reference", "Civil suit"],
          rows: [
            ["Court fee", "None", "Payable on the amount claimed"],
            ["Interest", "Compound, three times the RBI bank rate (Section 16)", "As agreed, or at the court’s discretion"],
            ["Process", "Conciliation, then arbitration", "Trial, or summary procedure for written contracts"],
            ["Challenging the outcome", "Buyer must deposit 75% of the award first", "Ordinary appeal"],
            ["Key condition", "Udyam registration before the contract", "None"],
          ],
        },
      },
      {
        heading: 'How do I register on Udyam, and when should I do it?',
        body: [
          '[Udyam registration](/guides/udyam-registration-for-freelancers-india) is free on the official portal, udyamregistration.gov.in, and uses your Aadhaar and PAN details. For a sole proprietor it usually takes minutes once the documents are in hand.',
          'Register before you sign the [contract](/guides/freelance-contract-template-india) you may later want to enforce, not after a client stops paying. Under the strict reading of Silpi, registering after the contract does not make that contract eligible, so the value of the registration is mostly for future work.',
        ],
      },
      {
        heading: 'What are the steps to file a reference on MSME Samadhaan?',
        body: [
          'The general order is: confirm you hold a valid Udyam registration that predates the contract; gather the agreement, invoices, proof of delivery and proof of non-payment; send the buyer a [demand notice](/guides/legal-notice-unpaid-freelance-work-india) and note the date; then register on the portal and file a reference with the Facilitation Council that has jurisdiction over the supplier\'s location.',
          'The form asks for your Udyam details, the buyer\'s details, the invoice amounts and dates, and the documents. The portal and its requirements change, so follow the current screens and instructions rather than any fixed checklist.',
        ],
        steps: [
          "Confirm your Udyam registration predates the contract.",
          "Gather the agreement, invoices, proof of delivery and proof of non-payment.",
          "Send the buyer a demand and note the date.",
          "Register on the MSME Samadhaan portal and file the reference with the Council for your location.",
          "Upload the documents and track the case on the portal.",
        ],
      },
      {
        heading: 'What happens after the reference is filed?',
        body: [
          'The Council issues notice to the buyer and calls the parties for conciliation. Section 18 contemplates the Council deciding the matter within 90 days of the reference, though in practice timelines vary by state and workload.',
          'If conciliation fails, the Council itself or an institution it refers the matter to acts as arbitrator, and the award is enforceable as a decree. A buyer who wants to challenge it must meet the 75% deposit condition first.',
        ],
      },
      {
        heading: 'What are the limits and risks of the MSEFC route?',
        body: [
          'Eligibility is the main risk: if your registration postdates the contract, the buyer can raise this as a defence, and the law on this point is still being settled. The buyer may also argue the Act does not apply to your kind of supply, or dispute the amount.',
          'It is also slower than a demand and not a guaranteed route to payment. Treat it as a strong option for eligible freelancers, and keep a civil suit under Order XXXVII CPC as your fallback. The [steps to raise a dispute](/guides/raise-dispute-against-non-paying-client-india) show how the options fit together.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is MSEFC only for manufacturers, or can service providers use it?',
        a: 'Service providers can use it. The MSMED Act covers micro and small enterprises engaged in manufacturing and in providing services, provided they hold the required registration.',
      },
      {
        q: 'Do I need a lawyer to file on MSME Samadhaan?',
        a: 'No. A supplier can file a reference in its own name. A lawyer is optional, and is more useful if the buyer contests eligibility or the matter proceeds to arbitration.',
      },
      {
        q: 'Can I register on Udyam after the client stops paying and still file?',
        a: 'It is risky. Under Silpi Industries (2021) the supplier must be registered when the contract is made, and later registration is not treated as covering that contract. A larger bench is reconsidering the point, but you should not rely on that.',
      },
      {
        q: 'What interest can I get through the MSEFC?',
        a: 'Section 16 of the MSMED Act provides compound interest with monthly rests at three times the bank rate notified by the RBI, on the amount unpaid after the permitted period.',
      },
    ],
  },
  {
    slug: 'freelance-contract-template-india',
    category: 'Contracts',
    title: 'Freelance Contract Template for India | Defellix',
    metaDescription:
      'Freelance contract format for India: the clauses to include, sample wording to adapt, plus e-signing and stamp duty basics.',
    h1: 'Freelance contract template and format for India',
    standfirst:
      'A good freelance contract in India has about ten parts: parties, scope and exclusions, payment milestones, late-payment interest, revisions, intellectual property, confidentiality, termination, governing law and signing. None needs stamp paper or a notary to be valid, but the clauses decide whether the contract is useful when a client stops paying.',
    updated: '2026-10-17',
    related: ['scope-creep-freelance-contract-clause', 'advance-payment-freelancers-india', 'who-owns-freelance-work-copyright-india'],
    sections: [
      {
        heading: 'What should a freelance contract in India include?',
        body: [
          'At minimum: the names and addresses of both parties, a description of the work and what is excluded, the fee and when each part is payable, a late-payment clause, a revision limit, who owns the work and when, confidentiality, how either side can end the agreement, and which law and courts apply.',
          'Add the signing method and the date. A short contract that covers these points specifically is better than a long one full of general language.',
        ],
        bullets: [
          "Parties: names, addresses and email addresses.",
          "Scope of work and a list of exclusions.",
          "Fee, milestones and payment due dates.",
          "Late-payment interest rate.",
          "Revision limit and the rate for extra rounds.",
          "Intellectual property and when it transfers.",
          "Confidentiality.",
          "Termination and payment for accepted work.",
          "Governing law and the courts of a named city.",
          "Signing method and date.",
        ],
      },
      {
        heading: 'How should I word the scope of work and exclusions?',
        body: [
          'Describe deliverables as specifics: formats, quantities, pages, features and the standard that counts as complete. Then state what is not included. Sample wording: “The Services consist of [deliverables]. Anything not listed in this clause, including [examples], is outside the scope and will be quoted separately in writing.”',
          'The exclusions line protects you from [scope creep](/guides/scope-creep-freelance-contract-clause), because it turns an extra request into a priced change request instead of an argument.',
        ],
      },
      {
        heading: 'How do I write payment terms and milestones?',
        body: [
          'Tie each payment to a dated milestone and an amount, starting with an [advance payment](/guides/advance-payment-freelancers-india). Sample wording: “The Fee is ₹[total], payable as follows: ₹[x] on signing, ₹[y] on delivery of [milestone], and ₹[z] on final acceptance. Each invoice is payable within [7/15] days of issue.”',
          'State whether the fee is inclusive or exclusive of [GST](/guides/gst-for-freelancers-india), and that payment is net of any statutory deduction with the certificate to be provided. Without it, the freelancer often ends up absorbing the tax.',
        ],
      },
      {
        heading: 'What late-payment clause should a freelance contract have?',
        body: [
          'Agree a rate before work starts. Sample wording: “Any amount not paid by its due date carries simple interest at [x]% per annum from the due date until payment, without prejudice to the Service Provider\'s other rights.”',
          'This matters because interest you did not agree is not something you can simply charge later. A court may allow interest under the Interest Act, 1978, but a stated rate makes it a contractual entitlement. If a client still does not pay, see [what a freelancer can do when a client does not pay](/guides/client-not-paying-freelancer-india).',
        ],
      },
      {
        heading: 'How should revisions and intellectual property be handled?',
        body: [
          'Cap the revisions: “The Fee includes [two] rounds of revisions. Further revisions are charged at ₹[rate] per hour.” Without a number, a fixed-price project can become unlimited unpaid work.',
          'For ownership, say [who owns the deliverables](/guides/who-owns-freelance-work-copyright-india) and when it passes. Sample wording: “Ownership of the final deliverables transfers to the Client on receipt of full payment. The Service Provider retains pre-existing tools and know-how.” Tying the transfer to full payment protects you until the client has paid.',
        ],
      },
      {
        heading: 'What about confidentiality, termination and governing law?',
        body: [
          'Confidentiality: each party keeps the other\'s non-public information private for a stated period (see the [NDA guide for freelancers](/guides/nda-for-freelancers-india)). Termination: allow either side to end with written notice, and state that work accepted up to that date remains payable. Governing law: Indian law, with courts at a named city.',
          'Naming a specific city gives you a clear forum. If you may later use the [MSEFC route](/guides/msefc-msme-samadhaan-freelancers-india), the Council that has jurisdiction is generally the one where the supplier is located.',
        ],
      },
      {
        heading: 'Is an e-signed freelance contract valid, and do I need stamp duty?',
        body: [
          'An [electronic signature is valid](/guides/electronic-signature-legal-validity-india) under Section 5 of the Information Technology Act, 2000, and a contract formed electronically is not unenforceable for that reason under Section 10A. An OTP sent to the signer\'s own email, recorded with a timestamp, is a common and acceptable method for an ordinary services agreement.',
          'Stamp duty is a separate question governed by the Indian Stamp Act, 1899 and your state\'s rules. Registration is not required for an ordinary services agreement, but an insufficiently stamped instrument can be inadmissible in evidence until the duty and any penalty are paid, so check your state before you rely on it in a dispute.',
        ],
      },
    ],
    faq: [
      {
        q: 'Is there a standard freelance contract format in India?',
        a: 'There is no prescribed statutory format. A freelance agreement is valid if it meets the requirements of the Indian Contract Act, 1872: offer, acceptance, lawful consideration, free consent and a lawful object. The clauses you include decide how well it protects you.',
      },
      {
        q: 'Do I need a lawyer to draft a freelance contract?',
        a: 'Not necessarily. For standard projects a clear template that you adapt can be enough. Consider a lawyer for high-value work, unusual intellectual-property arrangements or cross-border clients.',
      },
      {
        q: 'Should a freelancer get a contract even for a small project?',
        a: 'Yes. A short written agreement with scope, price, payment dates and a revision limit costs little and gives you something specific to point to if there is a disagreement.',
      },
      {
        q: 'Can I use the same contract for every client?',
        a: 'You can reuse a base template, but update the scope, amounts, dates and parties each time. A generic copy that does not match the actual project weakens your position.',
      },
    ],
  },
  {
    slug: 'whatsapp-agreement-legally-valid-india',
    category: 'Evidence and law',
    title: 'Is a WhatsApp Agreement Legally Valid in India? | Defellix',
    metaDescription:
      "A WhatsApp agreement can be a valid contract in India. Learn when it binds, how to prove it in court, and why a signed contract is still safer.",
    h1: 'Is a WhatsApp agreement legally valid in India?',
    standfirst:
      "Yes, a WhatsApp agreement can be a legally valid contract in India. The Indian Contract Act, 1872 does not require a contract to be on paper or signed, so an offer and acceptance exchanged over chat can bind both sides. The real difficulty is proof: showing months later exactly what was agreed, by whom, and that the chat has not been altered.",
    updated: '2026-10-17',
    related: ['electronic-signature-legal-validity-india', 'freelance-contract-india', 'client-ghosted-freelancer-india'],
    sections: [
      {
        heading: 'Does a WhatsApp chat count as a contract under Indian law?',
        body: [
          "It can. Section 10 of the Indian Contract Act, 1872 treats an agreement as a contract when it is made with free consent, between parties competent to contract, for lawful consideration and a lawful object. No particular form is required for an ordinary services agreement, and oral or chat-based agreements are valid.",
          "The Information Technology Act, 2000 supports this. Section 4 gives legal recognition to electronic records, and Section 10A says a contract is not unenforceable merely because it was formed electronically. A message that says “₹40,000 for the website, half upfront” followed by “Done, go ahead” has the ingredients of a contract.",
        ],
      },
      {
        heading: 'What makes a WhatsApp agreement weak in a dispute?',
        body: [
          "Vagueness and gaps. Chat agreements are usually spread across many messages, voice notes and calls, with the scope, price, deadline and revision count never stated in one place. Each gap becomes an argument about what was actually agreed.",
          "Identity and integrity are the other problems. You may need to show that the number belonged to the client, that the person typing had authority to commit the company, and that nothing was deleted or edited. Disappearing messages and “delete for everyone” make this harder.",
        ],
      },
      {
        heading: 'Are WhatsApp messages admissible as evidence in Indian courts?',
        body: [
          "Yes, as electronic records, but with a formality. Section 63 of the Bharatiya Sakshya Adhiniyam, 2023, which replaced Section 65B of the Indian Evidence Act, requires a certificate to accompany an electronic record produced as evidence. In Arjun Panditrao Khotkar v. Kailash Kushanrao Gorantyal (2020), the Supreme Court held the equivalent certificate under the earlier law to be mandatory.",
          "Screenshots on their own are the weakest form. Keep the original chat on the device, export the chat with media, and note the phone numbers and dates. The [certificate under Section 63](/guides/electronic-signature-legal-validity-india) is what lets a court rely on the record.",
        ],
      },
      {
        heading: 'How do I make a WhatsApp agreement stronger?',
        body: [
          "Put the whole deal in one message and ask for a clear reply. State the deliverables, the price, the payment dates, the deadline and the number of revisions, then ask the client to reply “Confirmed”. A single consolidated message is far easier to prove than forty scattered ones.",
          "Then follow up by email with the same terms, because email carries headers and is easier to authenticate. Turn off disappearing messages for that chat, and back it up before you change phones.",
        ],
        steps: [
          "Write the whole deal in one message: deliverables, price, payment dates, deadline and revisions.",
          "Ask the client to reply “Confirmed”.",
          "Send the same terms by email, which is easier to authenticate.",
          "Turn off disappearing messages for that chat.",
          "Export and back up the chat before changing phones.",
        ],
      },
      {
        heading: 'When should I use a proper contract instead of WhatsApp?',
        body: [
          "Use a written contract whenever the amount matters to you, the work runs longer than a few days, or intellectual property is involved. Copyright assignments in particular must be in writing and signed under Section 19 of the Copyright Act, 1957, so a chat is not a reliable way to transfer ownership.",
          "A short signed agreement also lets you include things a chat never does: a late-payment interest rate, a revision cap and a jurisdiction clause. See [how to make a freelance contract in India](/guides/freelance-contract-india) for the clauses that matter.",
        ],
        table: {
          head: ["", "WhatsApp agreement", "Signed contract"],
          rows: [
            ["Valid as a contract", "Can be", "Yes"],
            ["Proving what was agreed", "Hard, terms are scattered", "Easy, terms are in one document"],
            ["Revision limit, interest, jurisdiction", "Rarely included", "Included"],
            ["Copyright assignment", "Not reliable", "Valid when signed in writing"],
            ["Identity of the signer", "Must be proved from the phone number", "Recorded at signing"],
          ],
        },
      },
      {
        heading: 'Can a legal notice be sent over WhatsApp?',
        body: [
          "It can support service, but do not rely on it alone. Courts have in some cases accepted service through WhatsApp, including a 2018 Bombay High Court order that treated the delivery ticks as proof the message was received. Practice varies by court and by the rules that apply.",
          "The safer approach is to send a [demand notice](/guides/legal-notice-unpaid-freelance-work-india) by email and registered post, and use WhatsApp as an additional channel. Keep proof of each.",
        ],
      },
    ],
    faq: [
      {
        q: 'Is a WhatsApp message a valid contract in India?',
        a: "It can be. If the messages show an offer, an acceptance, lawful consideration and free consent between competent parties, the agreement is a contract under the Indian Contract Act, 1872 even though nothing was signed.",
      },
      {
        q: 'Are WhatsApp screenshots accepted as proof in court?',
        a: "They can be produced, but an electronic record needs a certificate under Section 63 of the Bharatiya Sakshya Adhiniyam, 2023 to be relied on. Preserving the original chat and export is stronger than screenshots alone.",
      },
      {
        q: 'Can a client deny a WhatsApp agreement?',
        a: "A client can dispute what was agreed or whether the number was theirs. That is why a consolidated confirmation message, a follow-up email and a signed contract make the agreement much harder to deny.",
      },
      {
        q: 'Is a voice note or phone call a valid agreement?',
        a: "An oral agreement is valid in principle, but it is the hardest to prove. Confirm anything agreed on a call in writing the same day.",
      },
    ],
  },
  {
    slug: 'udyam-registration-for-freelancers-india',
    category: 'Getting paid',
    title: 'Udyam Registration for Freelancers: How & Why | Defellix',
    metaDescription:
      "Udyam registration is free for freelancers in India. See who qualifies, what you need, the steps on the official portal, and why timing matters.",
    h1: 'Udyam registration for freelancers in India: how and why',
    standfirst:
      "A freelancer in India can register as a micro enterprise on the Udyam portal for free, using an Aadhaar number and PAN. The main reason to do it is payment protection under the MSMED Act, 2006, including the Facilitation Council route for delayed payments. That protection is tied to being registered when the contract is made, so the right time to register is before your next project.",
    updated: '2026-10-17',
    related: ['msefc-msme-samadhaan-freelancers-india', 'client-not-paying-freelancer-india', 'gst-for-freelancers-india'],
    sections: [
      {
        heading: "What is Udyam registration?",
        body: [
          "Udyam registration is the Government of India's free, online registration of micro, small and medium enterprises under the MSMED Act, 2006. A registered enterprise receives an Udyam Registration Number and a certificate, which is the basis for the Act's delayed-payment protections.",
        ],
      },
      {
        heading: 'Can a freelancer register on Udyam?',
        body: [
          "Yes. A freelancer working as a sole proprietor is an enterprise providing services and can register on Udyam. You do not need a company, a shop licence or a GST number to apply as a proprietor, though the portal asks for PAN and uses GST details where you have them.",
          "Classification depends on investment in equipment and annual turnover. As revised from 1 April 2025, a micro enterprise is one with investment up to ₹2.5 crore and turnover up to ₹10 crore, which covers almost every individual freelancer.",
        ],
      },
      {
        heading: 'What are the benefits of Udyam registration for a freelancer?',
        body: [
          "The benefit that matters most is delayed-payment protection. Under Section 15 of the MSMED Act, 2006 a buyer must pay within the agreed period, capped at 45 days, and Section 16 provides compound interest at three times the RBI bank rate on late amounts. Disputes can go to the [Facilitation Council through MSME Samadhaan](/guides/msefc-msme-samadhaan-freelancers-india) without a court fee.",
          "There is also a tax effect on the buyer. A buyer who pays a micro or small enterprise late can lose the deduction for that expense until it is actually paid, which gives larger clients a reason to pay registered suppliers on time.",
        ],
      },
      {
        heading: 'What do I need before I start?',
        body: [
          "You need your Aadhaar number with a mobile number linked to it for the OTP, your PAN, your bank account details, your business address, and a short description of the services you provide. No documents are uploaded; the registration is based on self-declaration.",
          "Decide the name you trade under. For a proprietor this is usually your own name or a trade name, and it should match what appears on your invoices and contracts.",
        ],
        bullets: [
          "Aadhaar number with a linked mobile number for the OTP.",
          "PAN.",
          "Bank account details.",
          "Business address.",
          "A short description of your services.",
        ],
      },
      {
        heading: 'What are the steps to register on the Udyam portal?',
        body: [
          "Go to the official portal, udyamregistration.gov.in, choose the option for new entrepreneurs, and verify your Aadhaar with the OTP. Enter your PAN, then fill in your name, the type of organisation, address, bank details and the activity you carry on, choosing services and the industry code that best matches your work.",
          "Enter your investment and turnover, confirm the declaration and submit with a final OTP. The portal issues an Udyam Registration Number in the format UDYAM-XX-00-0000000 and a certificate you can download.",
        ],
        steps: [
          "Open udyamregistration.gov.in and choose the option for new entrepreneurs.",
          "Verify your Aadhaar with the OTP.",
          "Enter and validate your PAN.",
          "Fill in your name, organisation type, address, bank details and activity, choosing services.",
          "Enter your investment and turnover, accept the declaration and submit with the final OTP.",
          "Download the certificate with your Udyam Registration Number.",
        ],
      },
      {
        heading: 'Is Udyam registration really free?',
        body: [
          "Yes. Registration on the government portal is free and there is no renewal fee. Many websites with similar names charge for the same thing, so check that you are on the gov.in address before entering your Aadhaar details.",
          "You do not need an agent. The form takes about ten to fifteen minutes once you have the details ready.",
        ],
      },
      {
        heading: 'When should a freelancer register?',
        body: [
          "Before signing the contract you may later need to enforce. In Silpi Industries v. Kerala SRTC (2021), the Supreme Court held that a supplier must be registered on the date of entering into the contract to claim the Act's benefits. A 2025 decision, NBCC (India) Ltd v. State of West Bengal, questioned that view and referred it to a larger bench, so the point is not finally settled.",
          "The practical rule is simple: register now, and put your Udyam number in your contracts and invoices from then on.",
        ],
      },
      {
        heading: 'Does Udyam registration mean I need GST?',
        body: [
          "No. Udyam and GST are separate. GST registration depends on your turnover and the kind of supplies you make, as explained in the [GST guide for freelancers](/guides/gst-for-freelancers-india). You can hold an Udyam registration without being registered for GST.",
          "Udyam registration does not itself create a tax liability. It records your status as a micro, small or medium enterprise.",
        ],
      },
    ],
    faq: [
      {
        q: 'Is Udyam registration mandatory for freelancers?',
        a: "No, it is optional. It is worth doing because it is free and it is the basis for the delayed-payment protections under the MSMED Act, 2006.",
      },
      {
        q: 'Can I register on Udyam without a GST number?',
        a: "Yes, a proprietor who is not required to register for GST can still register on Udyam using Aadhaar and PAN.",
      },
      {
        q: 'How long does Udyam registration take?',
        a: "The online form usually takes ten to fifteen minutes, and the registration number is generated when you submit it.",
      },
      {
        q: 'Does Udyam registration help with a client who has already stopped paying?',
        a: "It is unlikely to help for that contract. Under the Silpi Industries ruling the registration must exist when the contract is made, so it mainly protects your future work.",
      },
    ],
  },
  {
    slug: 'gst-for-freelancers-india',
    category: 'Tax and invoicing',
    title: 'GST for Freelancers in India: Rules Explained | Defellix',
    metaDescription:
      "When a freelancer in India must register for GST, the ₹20 lakh limit, the 18% rate, foreign clients and LUT, and what a GST invoice must show.",
    h1: 'GST for freelancers in India: when it applies and what to do',
    standfirst:
      "A freelancer in India must register for GST once aggregate turnover from services crosses ₹20 lakh in a financial year, or ₹10 lakh in certain special category states. Most professional services are taxed at 18%. Income from foreign clients counts towards the limit but can usually be treated as a zero-rated export, so you may owe no tax on it if you follow the procedure.",
    updated: '2026-10-17',
    related: ['tds-on-freelancer-payments-india', 'freelance-contract-template-india', 'udyam-registration-for-freelancers-india'],
    sections: [
      {
        heading: 'When does a freelancer need GST registration?',
        body: [
          "Registration becomes compulsory under Section 22 of the Central Goods and Services Tax Act, 2017 when your aggregate turnover exceeds ₹20 lakh in a financial year, or ₹10 lakh in certain special category states. Aggregate turnover is counted across India under one PAN and includes exports and exempt supplies.",
          "Service providers below the limit are generally not required to register merely because they have clients in other states, as a notification exempts inter-state suppliers of services below the threshold. You can also register voluntarily below the limit.",
        ],
        table: {
          head: ["Situation", "GST position"],
          rows: [
            ["Turnover up to ₹20 lakh (₹10 lakh in certain special category states)", "Registration not compulsory"],
            ["Turnover above the limit", "Registration compulsory"],
            ["Clients in other states, turnover below the limit", "Generally exempt from compulsory registration"],
            ["Clients outside India", "Counts towards turnover; usually a zero-rated export under a Letter of Undertaking"],
            ["Turnover up to ₹50 lakh, local clients", "Optional 6% flat scheme, with no input tax credit"],
          ],
        },
      },
      {
        heading: 'What GST rate applies to freelance services?',
        body: [
          "Most professional, technical and creative services, including software development, design, writing, consulting and video editing, are taxed at 18%. Inside your state this is split into 9% CGST and 9% SGST; for a client in another state it is charged as 18% IGST.",
          "Say in your contract whether your fee is inclusive or exclusive of GST. If the contract is silent and you later cross the limit, you may end up paying the tax out of your own fee.",
        ],
      },
      {
        heading: 'How is income from foreign clients treated?',
        body: [
          "Services to a client outside India can qualify as an export of services under the Integrated Goods and Services Tax Act, 2017 when the supplier is in India, the recipient is outside India, the place of supply is outside India and the payment is received in convertible foreign exchange or in rupees where the RBI permits. Exports are zero-rated.",
          "A registered freelancer can export without charging tax by filing a Letter of Undertaking on the GST portal each financial year, or pay IGST and claim a refund. Export income still counts towards the ₹20 lakh limit.",
        ],
      },
      {
        heading: 'What must a GST invoice for freelance work contain?',
        body: [
          "A tax invoice must carry your name, address and GSTIN, a unique serial number, the date, the client's name and address and their GSTIN if registered, the Services Accounting Code, a description of the service, the taxable value, the rate and amount of each tax, the place of supply, and your signature or digital signature.",
          "For services the invoice must generally be issued within 30 days of the supply. If you are not registered, you issue an ordinary invoice without GST and must not collect tax.",
        ],
        bullets: [
          "Your name, address and GSTIN.",
          "A unique serial number and the date.",
          "The client's name and address, and GSTIN if registered.",
          "Services Accounting Code and a description of the service.",
          "Taxable value, rate and amount of each tax.",
          "Place of supply.",
          "Your signature or digital signature.",
        ],
      },
      {
        heading: 'Is there a simpler scheme for small service providers?',
        body: [
          "Yes. A notified scheme allows eligible service providers with turnover up to ₹50 lakh to pay tax at a flat 6% without charging GST separately. The trade-off is that you cannot claim input tax credit, cannot issue a tax invoice, and cannot make inter-state supplies under it.",
          "It suits freelancers whose clients are consumers or small local businesses. If your clients are registered businesses that want credit, the regular scheme is usually better.",
        ],
      },
      {
        heading: 'Which returns does a registered freelancer file?',
        body: [
          "A regular taxpayer files GSTR-1 for outward supplies and GSTR-3B to pay tax, with an annual return where applicable. Those with turnover up to ₹5 crore can opt for the quarterly return with monthly payment scheme.",
          "Returns are due even in a month with no invoices. Late filing attracts a fee and interest, so calendar the dates as soon as you register.",
        ],
      },
      {
        heading: 'How does GST interact with TDS and contracts?',
        body: [
          "GST and TDS are different taxes. GST is charged on top of your fee; TDS is deducted by the client from your fee and credited against your income tax. Where GST is shown separately on the invoice, TDS is normally deducted on the amount excluding GST. See [TDS on freelancer payments](/guides/tds-on-freelancer-payments-india).",
          "Write both points into your agreement using the [freelance contract template](/guides/freelance-contract-template-india), so neither side is surprised at invoice time.",
        ],
      },
    ],
    faq: [
      {
        q: 'Do freelancers have to pay GST in India?',
        a: "Only once registered. Registration is compulsory when aggregate turnover exceeds ₹20 lakh in a financial year, or ₹10 lakh in certain special category states. Below that you are not required to charge GST.",
      },
      {
        q: 'Do I need GST registration if all my clients are abroad?',
        a: "Export income counts towards the turnover limit, so you must register once you cross it. After registering, you can usually export without paying tax by filing a Letter of Undertaking.",
      },
      {
        q: 'Can I charge GST without a GSTIN?',
        a: "No. Only a registered person may collect GST. Collecting tax without registration is an offence under the GST law.",
      },
      {
        q: 'What is the SAC code for freelance services?',
        a: "It depends on the service. Services Accounting Codes are grouped by type of service, so check the code for your activity on the GST portal or with a tax professional before invoicing.",
      },
    ],
  },
  {
    slug: 'tds-on-freelancer-payments-india',
    category: 'Tax and invoicing',
    title: 'TDS on Freelancer Payments in India Explained | Defellix',
    metaDescription:
      "How TDS on freelance fees works in India: the 10% rate, the ₹50,000 threshold, who must deduct, Form 16A, and how to claim the credit back.",
    h1: 'TDS on freelancer payments in India: rates, limits and credit',
    standfirst:
      "When an Indian business pays a freelancer for professional services, it generally deducts tax at source at 10% once payments in the financial year cross ₹50,000. The deduction is not an extra tax: it is paid to the government against your PAN and you claim it as a credit when you file your return. The provisions were renumbered when the Income-tax Act, 2025 took effect on 1 April 2026.",
    updated: '2026-10-17',
    related: ['gst-for-freelancers-india', 'freelance-contract-template-india', 'client-not-paying-freelancer-india'],
    sections: [
      {
        heading: 'What is TDS on freelance income?',
        body: [
          "Tax deducted at source is income tax collected in advance by the person making a payment. The client deducts a percentage of your fee, deposits it with the government against your PAN, and pays you the balance. The amount deducted appears in your tax credit statement.",
          "It reduces the cash you receive now, not the fee you earned. When you file your return, the TDS is set off against your total tax, and any excess is refunded.",
        ],
      },
      {
        heading: 'What is the TDS rate on professional fees?',
        body: [
          "The rate on fees for professional services has been 10%, under the provision long known as Section 194J of the Income-tax Act, 1961. A lower rate of 2% applies to fees for technical services and a few other categories. From 1 April 2025 the threshold was raised to ₹50,000 in a financial year, from ₹30,000.",
          "The Income-tax Act, 2025 replaced the 1961 Act from 1 April 2026 and consolidated the TDS provisions under new section numbers. The rates and limits described here are the ones carried into it, but confirm the current section references before quoting them in a contract.",
        ],
        table: {
          head: ["Payment", "Rate", "Threshold"],
          rows: [
            ["Fees for professional services", "10%", "₹50,000 in a financial year"],
            ["Fees for technical services", "2%", "₹50,000 in a financial year"],
            ["No PAN furnished", "Generally 20%", "—"],
            ["Client outside India", "No Indian TDS", "—"],
          ],
        },
      },
      {
        heading: 'Which clients have to deduct TDS?',
        body: [
          "Companies, firms and LLPs deduct when the threshold is crossed. An individual or Hindu undivided family deducts under the professional-fees provision only if their own business turnover or professional receipts exceeded the audit-linked limits in the previous year, and not for payments made for personal purposes.",
          "Individuals outside that rule who pay a contractor or professional more than ₹50 lakh in a year deduct under a separate provision at a lower rate. A client outside India does not deduct Indian TDS.",
        ],
      },
      {
        heading: 'Is TDS deducted on the GST amount too?',
        body: [
          "Normally no. Where the GST component is shown separately on the invoice, tax is deducted on the fee excluding GST. If the invoice shows a single lump sum, the client may deduct on the whole amount.",
          "This is a good reason to show the tax separately on every invoice, as explained in the [GST guide for freelancers](/guides/gst-for-freelancers-india).",
        ],
      },
      {
        heading: 'What happens if I do not give my PAN?',
        body: [
          "The client must deduct at a higher rate, generally 20%, if you do not furnish a valid PAN. The credit can also fail to reach your account if the PAN is quoted wrongly.",
          "Put your PAN on your contract and on every invoice, and check that the name matches your PAN records.",
        ],
      },
      {
        heading: 'How do I get credit for the TDS deducted?',
        body: [
          "The client issues a TDS certificate, Form 16A, each quarter. Check it against your annual tax statement on the income tax portal to confirm the deduction was actually deposited against your PAN.",
          "Claim the credit in your income tax return for that year. If a client deducted tax but it does not appear in your statement, ask them to correct their TDS return; you cannot claim credit for an amount that was never deposited.",
        ],
        steps: [
          "Collect Form 16A from the client each quarter.",
          "Check it against your annual tax statement on the income tax portal.",
          "Ask the client to correct their TDS return if a deduction is missing.",
          "Claim the credit when you file your income tax return.",
        ],
      },
      {
        heading: 'How should TDS appear in a freelance contract?',
        body: [
          "State that the fee is subject to tax deduction at source as required by law, and that the client will provide the TDS certificate within the statutory time. This prevents a dispute over a “short” payment that is really a lawful deduction.",
          "A deduction is not a reason for a client to pay late or hold back more than the law allows. If the unpaid balance goes beyond TDS, see [what to do when a client is not paying](/guides/client-not-paying-freelancer-india).",
        ],
      },
    ],
    faq: [
      {
        q: 'How much TDS is deducted on freelance payments?',
        a: "Generally 10% on fees for professional services once payments from that client exceed ₹50,000 in the financial year, with 2% for fees for technical services.",
      },
      {
        q: 'Can a freelancer get a TDS refund?',
        a: "Yes. TDS is adjusted against your total tax when you file your return, and any excess over your actual liability is refunded.",
      },
      {
        q: 'Do foreign clients deduct TDS from Indian freelancers?',
        a: "No, Indian TDS rules apply to payers covered by Indian tax law. A foreign client may apply its own country's withholding rules, which is a separate matter.",
      },
      {
        q: 'Is TDS the same as GST?',
        a: "No. TDS is income tax deducted from your fee and credited to you. GST is a tax on the service that a registered freelancer charges on top of the fee.",
      },
    ],
  },
  {
    slug: 'client-ghosted-freelancer-india',
    category: 'Getting paid',
    title: 'Client Ghosted You After Delivery? What to Do | Defellix',
    metaDescription:
      "A client stopped replying after you delivered the work. Follow these steps in order: secure proof, follow up in writing, send a notice, then escalate.",
    h1: 'A client ghosted you after you delivered the work: what to do',
    standfirst:
      "When a client goes silent after delivery, act in a fixed order: secure proof of what you delivered, follow up in writing on a schedule, send a dated demand once the payment is overdue, and then escalate to a forum. Silence does not cancel the debt, and in India you generally have three years from the due date to claim it.",
    updated: '2026-10-17',
    related: ['raise-dispute-against-non-paying-client-india', 'legal-notice-unpaid-freelance-work-india', 'client-not-paying-freelancer-india'],
    sections: [
      {
        heading: "What does it mean when a client ghosts a freelancer?",
        body: [
          "A client ghosts a freelancer when they stop responding after the work is delivered, usually leaving an invoice unpaid. The client's silence does not cancel the debt: payment for work delivered and accepted remains owed and can be recovered.",
        ],
      },
      {
        heading: 'What should I do first when a client stops replying?',
        body: [
          "Secure your evidence before you send another message. Save the agreement or the messages that set the terms, the files you delivered with their dates, any approval or use of the work by the client, and your invoices. Export chats and keep original emails with their headers.",
          "Do this first because the relationship can change quickly. Shared drives get revoked, chats get deleted and accounts get closed. What you hold today is what you can prove later.",
        ],
        steps: [
          "Save the agreement, delivered files, approvals and invoices.",
          "Check that the payment is actually overdue.",
          "Send two or three written reminders over about two weeks.",
          "Send a formal demand notice with a deadline.",
          "Escalate to the MSEFC, a summary suit or a Lok Adalat.",
        ],
      },
      {
        heading: 'How should I follow up before escalating?',
        body: [
          "Follow up in writing, on a short schedule, with the same facts each time: the invoice number, the amount and the due date. A reminder on the due date, another a week later and a final one a week after that is reasonable. Use email as the main channel, with a message elsewhere pointing to it.",
          "Keep the tone neutral. Angry or threatening messages weaken your position and can be quoted back at you. A polite, dated trail of unanswered reminders is good evidence.",
        ],
      },
      {
        heading: 'Is the client actually ghosting, or is payment not yet due?',
        body: [
          "Check the due date before you treat it as a default. If your agreement gives the client thirty days from invoice, silence in week two is not a breach. A demand sent before the debt is due has no legal footing.",
          "If there is no payment date anywhere, rely on the invoice terms and the usual course of dealing, and make the due date explicit in your next project.",
        ],
      },
      {
        heading: 'When should I send a formal demand?',
        body: [
          "Send it once the amount is overdue and your reminders have gone unanswered. A [demand notice for unpaid freelance work](/guides/legal-notice-unpaid-freelance-work-india) sets out the contract, the delivery, the amount and a deadline, usually seven to fifteen days.",
          "Send it by email and by registered or speed post. You can send it yourself; no Indian law requires an advocate to sign it.",
        ],
      },
      {
        heading: 'Can I take the work down or withhold files?',
        body: [
          "It depends on what your contract says. If ownership passes only on full payment, the client has no right to use the final deliverables until they pay, and you can say so in writing. If you have not handed over source files, you are generally not obliged to do so before payment unless you agreed otherwise.",
          "Be careful with self-help. Accessing a client's systems to disable something, or deleting live work, can create liability for you. Assert your rights in writing instead. See [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'What are my options if the client still does not pay?',
        body: [
          "You can refer the matter to the Facilitation Council if you held an Udyam registration before the contract, file a summary suit for a debt on a written contract, or apply to a Lok Adalat. The order and the trade-offs are set out in [how to raise a dispute against a non-paying client](/guides/raise-dispute-against-non-paying-client-india).",
          "For small amounts, a clear demand and the Council route are usually the proportionate choices.",
        ],
      },
      {
        heading: 'How do I stop this happening on the next project?',
        body: [
          "Take part of the fee before you start, split the rest across milestones, and hold final files until the last payment clears. Put a late-payment interest rate and a payment date in writing.",
          "Each of these is a clause, not a negotiation tactic. The [advance payment guide](/guides/advance-payment-freelancers-india) and the [freelance contract template](/guides/freelance-contract-template-india) show the wording.",
        ],
      },
    ],
    faq: [
      {
        q: 'How long should I wait before taking action against a client who ghosted me?',
        a: "Wait until the payment is actually overdue, send two or three written reminders over about two weeks, and then send a formal demand with a deadline.",
      },
      {
        q: 'Can I name and shame a client who did not pay?',
        a: "It is risky. Public accusations can lead to a defamation claim and distract from the debt. A written demand and a proper forum are safer and more effective.",
      },
      {
        q: 'Does ghosting cancel the contract?',
        a: "No. The client's silence does not end their obligation to pay for work delivered and accepted. The debt stays recoverable within the limitation period.",
      },
      {
        q: 'How long do I have to recover the money?',
        a: "Generally three years under the Limitation Act, 1963, counted from when the payment became due.",
      },
    ],
  },
  {
    slug: 'scope-creep-freelance-contract-clause',
    category: 'Contracts',
    title: 'Scope Creep: Contract Clauses for Freelancers | Defellix',
    metaDescription:
      "Stop scope creep with clear contract wording: a defined scope, exclusions, a revision cap and a change request clause. Sample clauses included.",
    h1: 'How to stop scope creep with the right contract clauses',
    standfirst:
      "Scope creep is prevented in the contract, not in the argument that follows. Four clauses do the work: a specific scope, a list of exclusions, a cap on revisions, and a change request clause that prices anything extra before it is done. With these in place, an extra request becomes a quoted change instead of unpaid work.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'freelance-contract-india', 'advance-payment-freelancers-india'],
    sections: [
      {
        heading: 'What is scope creep in freelance work?',
        body: [
          "Scope creep is the gradual growth of a project beyond what was agreed, without a matching change in the fee or the deadline. It usually arrives as small requests: one more page, one more round of changes, a format nobody mentioned.",
          "Each request looks reasonable alone. Together they can double the work on a fixed price. The cause is almost always a scope that was described in general terms.",
        ],
        table: {
          head: ["Clause", "What it does"],
          rows: [
            ["Defined scope", "Lists deliverables in countable terms"],
            ["Exclusions", "States what is not included"],
            ["Revision cap", "Limits rounds of changes and prices extra ones"],
            ["Change request", "Requires a written quote before extra work starts"],
            ["Client delays", "Extends deadlines when inputs arrive late"],
          ],
        },
      },
      {
        heading: 'How should I define the scope so it cannot drift?',
        body: [
          "Describe deliverables as countable things: the number of pages, screens, videos or words, the formats, the platforms, and what counts as complete. Avoid phrases such as “website design” or “social media content” with no quantities.",
          "Sample wording: “The Services consist of the design and development of a five-page website (Home, About, Services, Blog index, Contact) in responsive HTML, delivered as source files and a deployed build.”",
        ],
      },
      {
        heading: 'Why do I need an exclusions clause?',
        body: [
          "Because silence is read against the freelancer. If the contract does not say something is excluded, a client can argue it was implied. Listing exclusions removes that argument.",
          "Sample wording: “The following are outside the scope unless agreed in writing: content writing, stock images, hosting, third-party licences, ongoing maintenance and changes requested after final acceptance.”",
        ],
      },
      {
        heading: 'How many revisions should a contract include?',
        body: [
          "State a number and define a round. Sample wording: “The Fee includes two rounds of revisions per deliverable. A round is one consolidated list of changes sent in a single message. Further rounds are charged at ₹[rate] per hour.”",
          "Defining a round matters as much as the number. Without it, five separate messages can be argued to be one round.",
        ],
      },
      {
        heading: 'What is a change request clause?',
        body: [
          "It is the rule for handling anything outside the scope. Sample wording: “Any work outside the Services is a Change Request. The Service Provider will quote the additional fee and time in writing, and will start that work only after the Client confirms the quote in writing.”",
          "This gives you a neutral script. You are not refusing the request; you are following the process both sides signed.",
        ],
      },
      {
        heading: 'How do deadlines and client delays fit in?',
        body: [
          "Tie your deadline to the client's inputs. Sample wording: “Timelines assume the Client provides content, access and feedback within three working days of request. Delays extend the timeline by the same period.”",
          "Add an acceptance rule so a project cannot stay open indefinitely: “A deliverable is deemed accepted if the Client raises no written objection within seven days of delivery.”",
        ],
      },
      {
        heading: 'What do I say when a client asks for extra work?',
        body: [
          "Acknowledge the request, point to the clause and quote. For example: “Happy to add that. It is outside the agreed scope, so here is the additional fee and timeline. Shall I go ahead?” Then wait for a written yes.",
          "Use the [freelance contract template](/guides/freelance-contract-template-india) to put these clauses in before the project starts, because they are much harder to add later.",
        ],
      },
    ],
    faq: [
      {
        q: 'How do I prevent scope creep as a freelancer?',
        a: "Define the scope in countable terms, list what is excluded, cap the revisions and include a change request clause that requires a written quote before extra work begins.",
      },
      {
        q: 'Can I charge extra for additional revisions?',
        a: "Yes, if your contract states how many revisions are included and the rate for further ones. Without that, it becomes a negotiation.",
      },
      {
        q: 'Is a verbal change to the scope binding?',
        a: "An oral variation can be valid in principle but is hard to prove. Confirm every change in writing, with the fee and the new deadline.",
      },
      {
        q: 'What if the client refuses to pay for extra work I already did?',
        a: "You will need to show they asked for it and that it was outside the agreed scope. Written change requests make that straightforward.",
      },
    ],
  },
  {
    slug: 'who-owns-freelance-work-copyright-india',
    category: 'Contracts',
    title: 'Who Owns Freelance Work? Copyright in India | Defellix',
    metaDescription:
      "Under Indian copyright law a freelancer usually owns the work until it is assigned in writing. See the rules, the exceptions and sample clauses.",
    h1: 'Who owns the work a freelancer creates? Copyright in India',
    standfirst:
      "Under the Copyright Act, 1957 the author of a work is its first owner, and a freelancer working under a contract for services is normally that author. Ownership passes to the client only through a written assignment signed by the freelancer. There are exceptions for certain commissioned works, such as photographs and films, so the contract should always state who owns what and when.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'nda-for-freelancers-india', 'freelance-contract-india'],
    sections: [
      {
        heading: 'Who owns the copyright in freelance work by default?',
        body: [
          "The freelancer, in most cases. Section 17 of the Copyright Act, 1957 makes the author the first owner of copyright. The rule that gives ownership to an employer applies to work made in the course of employment under a contract of service, which a freelance engagement is not.",
          "So if a contract says nothing, a client who paid for a logo, an article or software code generally receives a right to use it, not ownership of the copyright.",
        ],
      },
      {
        heading: 'Are there works the client owns automatically?',
        body: [
          "Yes. Under the proviso to Section 17, where a photograph is taken, a painting or portrait drawn, an engraving made or a cinematograph film made for valuable consideration at the instance of another person, that person is the first owner unless there is an agreement to the contrary.",
          "This matters for photographers and video professionals. If you want to keep rights in commissioned photographs or footage, the contract must say so.",
        ],
        table: {
          head: ["Type of work", "First owner if the contract is silent"],
          rows: [
            ["Code, designs, writing, illustrations", "The freelancer"],
            ["Commissioned photographs", "The client who commissioned them"],
            ["Commissioned film or video (cinematograph film)", "The client who commissioned it"],
            ["Commissioned painting, portrait or engraving", "The client who commissioned it"],
            ["Work by an employee in the course of employment", "The employer"],
          ],
        },
      },
      {
        heading: 'How is copyright transferred to a client?',
        body: [
          "By a written assignment. Section 19 requires an assignment to be in writing and signed by the assignor, and to identify the work and specify the rights assigned, the duration and the territorial extent. An email saying “it is all yours” is a poor substitute.",
          "The defaults are narrow. If the period is not stated, the assignment is deemed to be for five years; if the territory is not stated, it is deemed to extend within India. Rights not exercised within a year can lapse unless the assignment says otherwise.",
        ],
      },
      {
        heading: 'Should I assign the copyright or license it?',
        body: [
          "Assign when the client needs to own the work outright, such as a brand identity or bespoke software. License when you want to keep ownership and grant defined uses, such as stock-style illustrations or templates you reuse.",
          "A licence should state whether it is exclusive, the purposes, the territory and the term. Under Section 30 a licence must also be in writing and signed.",
        ],
      },
      {
        heading: 'When should ownership pass to the client?',
        body: [
          "On full payment. Sample wording: “Upon receipt of the Fee in full, the Service Provider assigns to the Client all copyright in the final Deliverables, worldwide and for the full term of copyright. Until then, the Client has no right to use the Deliverables.”",
          "This is the freelancer's strongest practical protection. A client who has not paid does not hold the rights to the work they are using.",
        ],
      },
      {
        heading: 'What should I keep for myself?',
        body: [
          "Keep your pre-existing material and your right to show the work. Sample wording: “The Service Provider retains all rights in pre-existing tools, code libraries, templates and know-how, and grants the Client a non-exclusive licence to use them as part of the Deliverables. The Service Provider may display the Deliverables in its portfolio.”",
          "If the project is confidential, agree whether portfolio use is allowed, as covered in the [NDA guide for freelancers](/guides/nda-for-freelancers-india).",
        ],
      },
      {
        heading: 'Do I have any rights after assigning the work?',
        body: [
          "Yes. Section 57 gives an author special rights that survive assignment: the right to claim authorship, and the right to object to distortion or mutilation of the work that would harm the author's honour or reputation.",
          "These do not let you block ordinary use or edits. They are a floor, not a way to keep control of assigned work.",
        ],
      },
    ],
    faq: [
      {
        q: 'Does the client own the work once they pay a freelancer?',
        a: "Not automatically. Payment usually gives a right to use the work. Ownership of copyright passes through a written, signed assignment, except for certain commissioned works such as photographs and films.",
      },
      {
        q: 'Can a freelancer use client work in a portfolio?',
        a: "Only if the contract allows it or the client agrees. Add a portfolio clause, and check any confidentiality obligations first.",
      },
      {
        q: 'Is a copyright assignment valid over email or WhatsApp?',
        a: "An assignment must be in writing and signed by the assignor. A properly signed agreement, including one signed electronically, is far safer than an informal message.",
      },
      {
        q: 'Who owns the source files?',
        a: "It depends on the contract. Final deliverables and source or working files are often treated differently, so state whether source files are included and at what price.",
      },
    ],
  },
  {
    slug: 'summary-suit-order-37-recover-money-india',
    category: 'Getting paid',
    title: 'Summary Suit Under Order 37 CPC: Recover Fees | Defellix',
    metaDescription:
      "How a summary suit under Order XXXVII CPC helps recover unpaid fees in India: who can file, the written contract requirement, steps and limits.",
    h1: 'Summary suit under Order XXXVII CPC: recovering unpaid fees',
    standfirst:
      "A summary suit under Order XXXVII of the Code of Civil Procedure, 1908 is a faster form of money suit for a debt or liquidated demand arising on a written contract. The defendant cannot defend as of right: they must first obtain the court's leave. For a freelancer with a signed agreement and a fixed unpaid amount, it is often the most direct court route.",
    updated: '2026-10-17',
    related: ['raise-dispute-against-non-paying-client-india', 'legal-notice-unpaid-freelance-work-india', 'msefc-msme-samadhaan-freelancers-india'],
    sections: [
      {
        heading: 'What is a summary suit?',
        body: [
          "It is a special procedure for certain money claims. In an ordinary suit the defendant files a defence and the case goes to trial. In a summary suit the defendant must apply for leave to defend, and if leave is refused the plaintiff is entitled to judgment without a full trial.",
          "The idea is to stop defendants with no real defence from delaying payment through years of procedure.",
        ],
      },
      {
        heading: 'Which claims qualify for a summary suit?',
        body: [
          "Order XXXVII covers suits on bills of exchange, hundis and promissory notes, and suits to recover a debt or liquidated demand in money payable by the defendant, with or without interest, arising on a written contract, on an enactment or on a guarantee.",
          "Two things follow for freelancers. There must be a written contract, and the amount must be fixed or calculable, such as an unpaid invoice under agreed terms. A claim for unliquidated damages does not qualify.",
        ],
      },
      {
        heading: 'Does my freelance agreement count as a written contract?',
        body: [
          "A signed services agreement that states the fee and payment terms is the clearest basis. An electronically signed agreement is a written contract for this purpose, as explained in the guide to [e-signed contracts in India](/guides/electronic-signature-legal-validity-india).",
          "Claims built only on invoices or on chat messages are less certain, and courts have differed on them. If the amount matters, a single signed document is what makes this route dependable.",
        ],
      },
      {
        heading: 'What are the steps in a summary suit?',
        body: [
          "The plaint is filed stating that the suit is under Order XXXVII and that no relief outside it is claimed. After the summons is served, the defendant has ten days to enter an appearance. The plaintiff then serves a summons for judgment, and the defendant has ten days from that to apply for leave to defend.",
          "If the defendant does not appear, or leave is refused, the court may pass a decree for the amount claimed with interest and costs.",
        ],
        steps: [
          "File a plaint stating it is a suit under Order XXXVII.",
          "The defendant enters appearance within ten days of service of summons.",
          "Serve the summons for judgment.",
          "The defendant applies for leave to defend within ten days.",
          "The court grants or refuses leave; if refused, it may pass a decree.",
        ],
      },
      {
        heading: 'When will a court grant leave to defend?',
        body: [
          "In IDBI Trusteeship Services Ltd v. Hubtown Ltd (2017), the Supreme Court set out the approach. A defendant with a substantial defence gets unconditional leave. Where the defence raises triable issues but leaves doubt, leave may be conditional, for example on depositing the amount. Where there is no real defence, leave is refused.",
          "A clear contract, proof of delivery and written acceptance by the client make it harder for the client to show a genuine defence.",
        ],
      },
      {
        heading: 'What does it cost and how long do I have?',
        body: [
          "Court fees are payable on the amount claimed, at rates fixed by each state, in addition to advocate fees. That makes the route better suited to larger sums than to very small invoices.",
          "The limitation period for a money claim is generally three years from when the amount became due. Where the claim is a commercial dispute of ₹3 lakh or more, Section 12A of the Commercial Courts Act, 2015 requires pre-institution mediation first, unless urgent interim relief is sought.",
        ],
      },
      {
        heading: 'Summary suit or the MSEFC route?',
        body: [
          "If you were registered on Udyam before the contract, the [Facilitation Council route](/guides/msefc-msme-samadhaan-freelancers-india) is usually cheaper because there is no court fee and the interest is higher. If you were not, the summary suit is the natural court remedy for a fixed sum on a written contract.",
          "Either way, start with a [demand notice](/guides/legal-notice-unpaid-freelance-work-india). It is often enough, and it supports your claim for interest and costs.",
        ],
      },
    ],
    faq: [
      {
        q: 'Can a freelancer file a summary suit for unpaid fees?',
        a: "Yes, where the claim is for a fixed amount of money arising on a written contract, which a signed freelance agreement with stated fees can satisfy.",
      },
      {
        q: 'Is a summary suit faster than a normal civil suit?',
        a: "It is designed to be, because the defendant must obtain leave to defend. Actual timelines depend on the court and on whether leave is granted.",
      },
      {
        q: 'Do I need a lawyer to file a summary suit?',
        a: "A party may appear in person, but the procedure is technical and most people engage an advocate for drafting and appearances.",
      },
      {
        q: 'Can I file a summary suit based on WhatsApp messages?',
        a: "It is uncertain. The procedure requires a written contract, and courts have taken different views on informal records. A signed agreement is the safer basis.",
      },
    ],
  },
  {
    slug: 'advance-payment-freelancers-india',
    category: 'Contracts',
    title: 'Advance Payment for Freelancers: How to Ask | Defellix',
    metaDescription:
      "How Indian freelancers should ask for an advance: how much to charge, milestone structures that work, refund wording, and the contract clauses.",
    h1: 'Advance payment for freelancers: how much, and how to ask',
    standfirst:
      "Most freelancers should take part of the fee before starting, commonly 30% to 50%, and split the rest across milestones with the final payment due before the final files are released. An advance is the simplest protection against non-payment, and clients accept it more readily when it is tied to a defined first phase of work and written into the contract.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'scope-creep-freelance-contract-clause', 'client-ghosted-freelancer-india'],
    sections: [
      {
        heading: 'Should a freelancer ask for an advance?',
        body: [
          "Yes, on almost every project. An advance confirms the client is serious, covers your time for the first phase, and limits how much you can lose if they disappear. A client unwilling to pay anything upfront is telling you something useful before you start.",
          "It is normal commercial practice. Framed as the first milestone of the project and not as a deposit of trust, it rarely causes friction.",
        ],
      },
      {
        heading: 'How much advance should I charge?',
        body: [
          "For small projects, 50% before work starts and 50% before final delivery is common. For larger ones, 30% to 40% upfront with the balance spread over milestones works better. For very short jobs or new clients, full payment in advance is reasonable.",
          "Set the figure by exposure. Ask how much unpaid work you would have done before the next payment falls due, and keep that number tolerable.",
        ],
        table: {
          head: ["Project", "Typical structure"],
          rows: [
            ["Small or short project", "50% upfront, 50% before final delivery"],
            ["Larger project", "30–40% upfront, balance over milestones"],
            ["New client or very short job", "Full payment upfront"],
            ["Long engagement", "Monthly or milestone billing in advance"],
          ],
        },
      },
      {
        heading: 'How should I structure milestone payments?',
        body: [
          "Tie each payment to something the client can see and approve. Sample wording: “The Fee is payable as follows: 40% on signing; 30% on approval of the first draft; 30% before release of the final files. Each invoice is payable within seven days.”",
          "Keep the last payment before final handover, not after. A balance that falls due after the client already has everything is the one most often left unpaid.",
        ],
      },
      {
        heading: 'Is an advance refundable if the project is cancelled?',
        body: [
          "It depends on the wording and on how much work was done. A clause that keeps the whole advance regardless of the work can be challenged as a penalty. Under Section 74 of the Indian Contract Act, 1872 a party is entitled to reasonable compensation, not an arbitrary forfeiture.",
          "Safer wording ties the advance to work: “The advance covers the discovery and first-draft phase. If the Client cancels, the advance is retained to the extent of work completed and committed time, and any balance is refunded.”",
        ],
      },
      {
        heading: 'How do I ask for an advance without losing the client?',
        body: [
          "State it as your standard terms, in the proposal, before any discussion of price. For example: “I work on a 40/30/30 milestone basis, with the first payment due on signing.” Avoid apologising for it or presenting it as optional.",
          "If a client pushes back, reduce the first milestone, not the principle. A smaller first phase with a smaller payment keeps you protected.",
        ],
      },
      {
        heading: 'What should the contract say about late milestone payments?',
        body: [
          "Give yourself the right to pause. Sample wording: “If any payment is overdue by more than seven days, the Service Provider may suspend work until it is received, and timelines extend by the period of suspension.”",
          "Add a late-payment interest rate as well. Both clauses, with the rest of the agreement, are in the [freelance contract template](/guides/freelance-contract-template-india).",
        ],
      },
    ],
    faq: [
      {
        q: 'What percentage advance do freelancers usually take in India?',
        a: "Commonly between 30% and 50% of the fee before starting, with the balance tied to milestones and the final payment due before final files are released.",
      },
      {
        q: 'Can I keep the advance if the client cancels?',
        a: "You can generally retain it to the extent of work done and time committed. A blanket non-refundable clause is open to challenge, so tie the advance to a defined phase of work.",
      },
      {
        q: 'Should I charge GST on an advance?',
        a: "If you are registered for GST, tax treatment of advances for services has its own rules, so confirm the timing with a tax professional. See the GST guide for the basics.",
      },
      {
        q: 'What if a client refuses to pay any advance?',
        a: "Offer a smaller first milestone or a paid trial phase. If they still refuse any upfront payment, weigh the risk carefully before starting.",
      },
    ],
  },
  {
    slug: 'nda-for-freelancers-india',
    category: 'Contracts',
    title: 'NDA for Freelancers in India: What to Check | Defellix',
    metaDescription:
      "What a freelancer should check before signing an NDA in India: the definition, exclusions, term, non-compete clauses and what Indian law allows.",
    h1: 'NDA for freelancers in India: what to check before you sign',
    standfirst:
      "A non-disclosure agreement is enforceable in India as an ordinary contract, and a freelancer can sign one safely if five things are right: a clear definition of confidential information, the standard exclusions, a fixed term, no hidden non-compete, and a carve-out for your own tools and portfolio. Restraints that stop you working for others after the project are generally void under Section 27 of the Indian Contract Act, 1872.",
    updated: '2026-10-17',
    related: ['who-owns-freelance-work-copyright-india', 'freelance-contract-template-india', 'freelance-contract-india'],
    sections: [
      {
        heading: "What is an NDA for freelancers?",
        body: [
          "A non-disclosure agreement is a contract in which a freelancer agrees to keep a client's confidential information private and to use it only for the project. It can be a separate document or a confidentiality clause inside the main services agreement.",
        ],
      },
      {
        heading: 'Is an NDA legally enforceable in India?',
        body: [
          "Yes. India has no separate trade secrets statute, so confidentiality is protected mainly through contract and through the courts' equitable jurisdiction over breach of confidence. A properly drafted NDA is enforceable like any other agreement under the Indian Contract Act, 1872.",
          "Remedies for breach include an injunction to stop further disclosure and damages for loss, which is why the definition of what is protected needs to be precise.",
        ],
      },
      {
        heading: 'How should confidential information be defined?',
        body: [
          "Narrowly enough that you can comply. Look for a definition tied to information disclosed for the project and marked or reasonably understood as confidential. Be cautious with wording that covers “all information of any kind” with no limit.",
          "A definition you cannot work with in practice is a risk for you, because almost anything you later do could be said to use it.",
        ],
      },
      {
        heading: 'What exclusions should every NDA have?',
        body: [
          "Four are standard: information already public, information you already knew, information you developed independently, and information you received lawfully from a third party. There should also be a carve-out for disclosure required by law or a court.",
          "If these are missing, ask for them. They are routine and a reasonable client will not object.",
        ],
        bullets: [
          "Information that is already public.",
          "Information you already knew.",
          "Information you developed independently.",
          "Information received lawfully from a third party.",
          "Disclosure required by law or a court.",
        ],
      },
      {
        heading: 'How long should the confidentiality obligation last?',
        body: [
          "A fixed period, commonly two to five years after the project ends, is typical for ordinary business information. Genuine trade secrets may be protected for as long as they remain secret.",
          "Be wary of obligations stated to last forever for everything. A defined term is easier to comply with and to enforce.",
        ],
      },
      {
        heading: 'Can an NDA stop me working for a competitor?',
        body: [
          "Generally not after the engagement ends. Section 27 of the Indian Contract Act, 1872 makes an agreement that restrains anyone from exercising a lawful profession, trade or business void to that extent. In Superintendence Company of India v. Krishan Murgai (1980) and Percept D'Mark (India) v. Zaheer Khan (2006), the Supreme Court treated post-termination restraints as void.",
          "Restrictions that apply only during the engagement, and duties not to misuse confidential information, are treated differently. Read any non-compete or non-solicitation clause placed inside an NDA carefully.",
        ],
      },
      {
        heading: 'What should a freelancer add to a client NDA?',
        body: [
          "Ask for the NDA to be mutual if you are sharing your own methods or pricing. Add that your pre-existing tools, code and general skills are not the client's confidential information, and agree whether you may list the client or show the work in your portfolio.",
          "Ownership of what you create is a separate question from confidentiality. Deal with it expressly, as explained in [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'Do I need a separate NDA or is a contract clause enough?',
        body: [
          "For most projects a confidentiality clause inside the services agreement is enough. A separate NDA makes sense when information is shared before the engagement is agreed, such as during a pitch.",
          "The [freelance contract template](/guides/freelance-contract-template-india) includes a confidentiality clause you can adapt.",
        ],
      },
    ],
    faq: [
      {
        q: 'Should a freelancer sign an NDA?',
        a: "Usually yes, if the definition of confidential information is reasonable, the standard exclusions are included, the term is fixed and there is no hidden non-compete.",
      },
      {
        q: 'Is a non-compete clause valid for freelancers in India?',
        a: "A restraint that applies after the engagement ends is generally void under Section 27 of the Indian Contract Act, 1872. Restrictions during the engagement may be enforceable.",
      },
      {
        q: 'Does an NDA need to be on stamp paper?',
        a: "An NDA is valid without stamp paper, but stamp duty may apply under the Indian Stamp Act, 1899 and state law, which can affect its admissibility in evidence until duty is paid.",
      },
      {
        q: 'Can I show NDA-covered work in my portfolio?',
        a: "Only with the client's permission or an express portfolio clause. Without it, showing the work may breach the NDA.",
      },
    ],
  },
  {
    slug: 'freelance-web-developer-contract-india',
    category: 'Contracts',
    template: 'web-developer',
    title: 'Web Developer Contract Template (India) | Defellix',
    metaDescription:
      "A freelance web developer contract for India: clauses to include, source code and hosting terms, milestones, revisions, and a template to start from.",
    h1: 'Freelance web developer contract in India: template and clauses',
    standfirst:
      "A freelance web developer contract in India should name the pages and features you will build, list what is excluded, split the fee into milestones, cap revisions, say who owns the source code and when, set a bug-fix period, and cover hosting, credentials and third-party code. It is valid when signed electronically, and the template below gives you every clause to adapt.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'who-owns-freelance-work-copyright-india', 'scope-creep-freelance-contract-clause'],
    sections: [
      {
        heading: 'What is a freelance web developer contract?',
        body: [
          "A freelance web developer contract is a services agreement between a developer and a client that fixes what will be built, the price and payment dates, who owns the code, and what happens after launch. In India it is governed by the Indian Contract Act, 1872, and an electronically signed version is valid under Section 5 of the Information Technology Act, 2000.",
        ],
      },
      {
        heading: 'Which clauses does a web development contract need?',
        body: [
          "Web projects fail on the same few points: features that were never written down, launch dates that slip because the client is late with content, and arguments about code and credentials after the last payment. These clauses cover them.",
        ],
        table: {
          head: ['Clause', 'What to write', 'Why it matters'],
          rows: [
            ['Scope', 'Pages, features, integrations and supported browsers or devices', 'Stops “one more feature” being treated as included'],
            ['Exclusions', 'Content, images, hosting fees, SEO, maintenance', 'Silence in a contract is read against the developer'],
            ['Milestones', 'Design, development and launch, each with an amount and date', 'Limits unpaid work if the client disappears'],
            ['Revisions', 'Rounds included per milestone, and the hourly rate after that', 'Prevents endless design changes'],
            ['Source code and IP', 'Ownership passes to the client on full payment', 'Code belongs to the developer until assigned in writing'],
            ['Hosting and credentials', 'Client owns hosting and domain; credentials handed over on final payment', 'Avoids lock-in disputes either way'],
            ['Third-party code', 'Open-source libraries keep their own licences', 'You cannot assign rights you do not own'],
            ['Bug-fix period', 'Free fixes for defects in scope for [number] days after launch', 'Separates warranty from new work'],
          ],
        },
      },
      {
        heading: 'How do I write a web development contract, step by step?',
        body: [
          "Work through these steps before you write any code.",
        ],
        steps: [
          "List every page and feature, and the technology you will use.",
          "List what is excluded, especially content, hosting and maintenance.",
          "Split the fee into milestones: design approval, development, launch.",
          "Set the revision rounds and the rate for extra changes.",
          "State that source code ownership passes to the client on full payment.",
          "Agree hosting, domain, credentials and the bug-fix period.",
          "Add late-payment interest, GST and TDS terms, and jurisdiction, then sign.",
        ],
      },
      {
        heading: 'Who owns the source code of a website built by a freelancer?',
        body: [
          "The freelancer, unless the copyright is assigned. Software is protected as a literary work under the Copyright Act, 1957, and Section 17 makes the author the first owner. The rule that hands ownership to an employer applies to employees, not independent freelancers.",
          "Ownership moves to the client only through a written, signed assignment under Section 19. Tie it to full payment, and keep your own reusable code and tools, licensing them to the client as part of the deliverable. See [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'How should payment milestones work for a website project?',
        body: [
          "Take an advance before starting, usually 30% to 50%, then tie the rest to milestones the client can see: approved designs, a working staging site, and launch. Keep the final payment before you hand over credentials and the production deployment.",
          "Add a right to pause if a milestone payment is more than seven days late. The [advance payment guide](/guides/advance-payment-freelancers-india) has sample wording.",
        ],
      },
      {
        heading: 'What mistakes do web developers make in contracts?',
        body: [
          "These are the gaps that turn into unpaid work or disputes most often.",
        ],
        bullets: [
          "Describing the project as “a website” with no page or feature list.",
          "No exclusions, so content, hosting and SEO become implied extras.",
          "Unlimited revisions on design.",
          "Handing over credentials and code before the final payment.",
          "No bug-fix period, so every later request is argued as a defect.",
          "Deadlines with no link to when the client supplies content.",
        ],
      },
      {
        heading: 'Where can I create a web developer contract online?',
        body: [
          "On Defellix you can start from a ready-made web developer template: the scope, exclusions, revision limit, source-code ownership and hosting clauses are filled in, and you replace the bracketed details. Your client signs by email OTP, and every milestone, approval and payment is recorded with a timestamp. Your first contract is free.",
        ],
      },
    ],
    faq: [
      {
        q: 'Do I need a contract for a small website project?',
        a: "Yes. Even a short agreement with the page list, price, payment dates and revision limit prevents most disputes, and it costs nothing to add.",
      },
      {
        q: 'Should I give the client the source code?',
        a: "Usually yes, but only after full payment, and the contract should say so. Until then the copyright in the code stays with you.",
      },
      {
        q: 'How much TDS will a client deduct from a web developer?',
        a: "It depends on how the payment is classified. Fees for professional services generally attract 10% and fees for technical services 2%, once the annual threshold is crossed. Confirm with the client and a tax professional.",
      },
      {
        q: 'What if the client keeps adding features?',
        a: "Point to the scope and exclusions, and quote each new feature as a change request before starting it. See the guide on scope creep for sample wording.",
      },
    ],
  },
  {
    slug: 'graphic-designer-contract-india',
    category: 'Contracts',
    template: 'graphic-designer',
    title: 'Graphic Designer Contract Template (India) | Defellix',
    metaDescription:
      "A graphic designer contract for India: concepts, revisions, source files, font and stock licences, usage rights, and a template to start from.",
    h1: 'Graphic designer contract in India: template and clauses',
    standfirst:
      "A freelance graphic designer contract in India should state the number of concepts and revision rounds, the final file formats, whether editable source files are included, who pays for fonts and stock images, and when the copyright in the final design passes to the client. Tie the transfer to full payment and keep unused concepts and portfolio rights for yourself.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'who-owns-freelance-work-copyright-india', 'advance-payment-freelancers-india'],
    sections: [
      {
        heading: 'What is a graphic designer contract?',
        body: [
          "A graphic designer contract is a services agreement between a designer and a client that sets out the design work, concepts and revisions, file formats, price and payment dates, and who owns the finished artwork. In India it is an ordinary contract under the Indian Contract Act, 1872, and it can be signed electronically.",
        ],
      },
      {
        heading: 'Which clauses matter most in a design contract?',
        body: [
          "Design disputes usually come from open-ended revisions, missing source files and unclear ownership. These clauses settle each one before you start.",
        ],
        table: {
          head: ['Clause', 'What to write', 'Why it matters'],
          rows: [
            ['Concepts', 'Number of initial concepts', 'More concepts means more unpaid work'],
            ['Revisions', 'Rounds included on the chosen concept, and the rate after', 'Stops endless “small tweaks”'],
            ['Final files', 'Formats and sizes, for print and web', 'Avoids arguments about what “final” means'],
            ['Source files', 'Included, or available for a fee', 'Source files are often worth more than the final export'],
            ['Fonts and stock', 'Client buys any paid licences', 'The licence must be in the client’s name'],
            ['Copyright', 'Final design assigned to the client on full payment', 'Copyright stays with the designer until assigned in writing'],
            ['Unused concepts', 'Remain the designer’s', 'Only the chosen design is transferred'],
            ['Portfolio', 'Designer may show the work after launch', 'Keeps your right to market your work'],
          ],
        },
      },
      {
        heading: 'Who owns a logo or design made by a freelancer?',
        body: [
          "The designer, until the copyright is assigned. Under Section 17 of the Copyright Act, 1957 the author of an artistic work is its first owner, and a freelance designer is not an employee. The client gets ownership only through a written, signed assignment under Section 19.",
          "This matters for logos in particular, because a client who wants to register the logo as a trademark needs clear ownership of it. Assign the final logo on full payment, and say that unused concepts stay with you. More detail is in [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'How do I write a design contract, step by step?',
        body: [
          "Settle these points before the first concept.",
        ],
        steps: [
          "Describe the deliverables, such as a logo, brand guide or social media kit.",
          "Set the number of concepts and revision rounds.",
          "List the final file formats and whether source files are included.",
          "Say who buys any paid fonts or stock images.",
          "Assign the copyright in the final design on full payment.",
          "Agree the fee, an advance and milestone dates, then sign.",
        ],
      },
      {
        heading: 'Should a designer hand over source files?',
        body: [
          "Only if the contract says so. Editable files let the client change the design without you, so many designers either include them in a higher price or charge separately. Whichever you choose, write it down, because a client who expected source files will treat their absence as a breach.",
        ],
      },
      {
        heading: 'What mistakes do designers make in contracts?',
        body: [
          "These come up again and again in design disputes.",
        ],
        bullets: [
          "No limit on concepts or revision rounds.",
          "Not defining what counts as one round of revisions.",
          "Silence on source files.",
          "Using paid fonts or stock images without saying who holds the licence.",
          "Transferring all rights before final payment.",
          "No portfolio clause.",
        ],
      },
      {
        heading: 'Where can I create a graphic designer contract online?',
        body: [
          "On Defellix you can start from a ready-made graphic designer template with the concepts, revisions, source-file, licence and ownership clauses already written, then replace the bracketed details. Your client signs by email OTP, and each approval and payment is recorded with a timestamp. Your first contract is free.",
        ],
      },
    ],
    faq: [
      {
        q: 'How many revisions should a graphic designer offer?',
        a: "Two or three rounds on the chosen concept is common. Define a round as one consolidated list of changes, and set a rate for further rounds.",
      },
      {
        q: 'Can a client use a design before paying?',
        a: "Not if your contract ties the copyright transfer to full payment. Until then the client has no right to use the final design.",
      },
      {
        q: 'Who pays for fonts used in a logo?',
        a: "The contract should say. Usually the client buys the font licence in its own name, because it will keep using the font after the project.",
      },
      {
        q: 'Can I reuse a rejected concept for another client?',
        a: "Yes, if your contract says unused concepts remain yours. Only the design the client chooses and pays for is assigned.",
      },
    ],
  },
  {
    slug: 'video-editor-contract-india',
    category: 'Contracts',
    template: 'video-editor',
    title: 'Video Editor Contract Template (India) | Defellix',
    metaDescription:
      "A video editor contract for India: deliverables, turnaround, revisions, music licensing, footage rights, project files, and a template to use.",
    h1: 'Video editor contract in India: template and clauses',
    standfirst:
      "A freelance video editor contract in India should set the number and length of videos, turnaround from receipt of footage, revision rounds, export formats, who is responsible for music and footage rights, and whether project files are handed over. Because the client usually owns the finished video, the contract should protect your payment and your right to show the work.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'who-owns-freelance-work-copyright-india', 'scope-creep-freelance-contract-clause'],
    sections: [
      {
        heading: 'What is a video editor contract?',
        body: [
          "A video editor contract is a services agreement between a freelance editor and a client that fixes the videos to be edited, the turnaround, revisions, delivery formats, price and payment dates, and responsibility for music and footage. It is governed by the Indian Contract Act, 1872, and can be signed electronically.",
        ],
      },
      {
        heading: 'Which clauses does a video editing contract need?',
        body: [
          "Video work goes wrong over turnaround, endless re-edits and music that was never licensed. These clauses cover them.",
        ],
        table: {
          head: ['Clause', 'What to write', 'Why it matters'],
          rows: [
            ['Deliverables', 'Number of videos, maximum length, style and platform', 'A “video” can mean anything without detail'],
            ['Turnaround', 'Working days from receiving complete footage', 'Late footage should not make you late'],
            ['Revisions', 'Rounds included per video, and the rate after', 'Re-edits are the main source of unpaid work'],
            ['Music and stock', 'Which libraries are covered; who pays for extra licences', 'Unlicensed music can get a video blocked'],
            ['Footage rights', 'Client confirms it owns the footage it supplies', 'Protects you if the client sends material it cannot use'],
            ['Project files', 'Included, or available for a fee', 'Raw project files let the client re-edit without you'],
            ['Ownership', 'Finished video belongs to the client on full payment', 'Ties ownership to payment'],
            ['Portfolio', 'Editor may show the video after it is published', 'Keeps your showreel rights'],
          ],
        },
      },
      {
        heading: 'Who owns a video edited by a freelancer?',
        body: [
          "Usually the client. Under the Copyright Act, 1957 the author of a cinematograph film is its producer, the person who takes the initiative and responsibility for making it, and the proviso to Section 17 gives the first ownership of a film made for valuable consideration at someone's instance to that person, unless agreed otherwise.",
          "In practice that means your leverage is payment, not ownership. Say in the contract that the client may use the final video only after full payment, and keep the right to show it in your showreel. See [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'How do I write a video editing contract, step by step?',
        body: [
          "Agree these points before you open the footage.",
        ],
        steps: [
          "Set the number of videos, the maximum length and the platform.",
          "Set turnaround in working days from receipt of complete footage.",
          "Fix the revision rounds and the rate for extra re-edits.",
          "Agree which music and stock libraries are covered.",
          "Have the client confirm it owns the footage it supplies.",
          "Say whether project files are included, and the price if not.",
          "Agree payment per video or per batch, with an advance, then sign.",
        ],
      },
      {
        heading: 'How should a video editor charge and get paid?',
        body: [
          "Charge per video for one-off work and per batch or month for regular channels. Take an advance on the first batch, and release final exports only after each payment clears. For monthly retainers, bill at the start of the month.",
          "Write a pause right into the contract for late payments, and see the [advance payment guide](/guides/advance-payment-freelancers-india) for sample wording.",
        ],
      },
      {
        heading: 'What mistakes do video editors make in contracts?',
        body: [
          "These cause most disputes in editing work.",
        ],
        bullets: [
          "Deadlines that start before the client sends all the footage.",
          "No cap on re-edits.",
          "Silence on who pays for music and stock footage.",
          "Handing over project files without agreeing a price.",
          "Delivering final exports before payment.",
          "No portfolio or showreel clause.",
        ],
      },
      {
        heading: 'Where can I create a video editor contract online?',
        body: [
          "On Defellix you can start from a ready-made video editor template with the turnaround, revision, music, footage and project-file clauses written in, then replace the bracketed details. Your client signs by email OTP, and every delivery and approval is recorded with a timestamp. Your first contract is free.",
        ],
      },
    ],
    faq: [
      {
        q: 'Does the client own the video I edit?',
        a: "Usually yes, because the client commissions and produces the film. Make the client's use of the final video conditional on full payment.",
      },
      {
        q: 'Should I give the client my project files?',
        a: "Only if the contract says so. Many editors charge extra for project files because they let the client re-edit without them.",
      },
      {
        q: 'Who is responsible if the music gets a copyright claim?',
        a: "The contract should say which music libraries are covered and that the client is responsible for any music it supplies or requests outside them.",
      },
      {
        q: 'How many revisions should a video editor include?',
        a: "One or two rounds per video is common. Define a round as one consolidated list of changes and charge for further rounds.",
      },
    ],
  },
  {
    slug: 'content-writer-contract-india',
    category: 'Contracts',
    template: 'content-writer',
    title: 'Content Writer Contract Template (India) | Defellix',
    metaDescription:
      "A content writer contract for India: briefs, word counts, revisions, bylines, originality, copyright and payment terms, with a template to use.",
    h1: 'Content writer contract in India: template and clauses',
    standfirst:
      "A freelance content writer contract in India should set the number of pieces and word counts, how briefs are agreed, revision rounds, originality and AI-use terms, whether you get a byline, and when the copyright passes to the client. Writers own their work until it is assigned in writing, so tie the transfer to payment and agree publishing rights up front.",
    updated: '2026-10-17',
    related: ['freelance-contract-template-india', 'who-owns-freelance-work-copyright-india', 'tds-on-freelancer-payments-india'],
    sections: [
      {
        heading: 'What is a content writer contract?',
        body: [
          "A content writer contract is a services agreement between a freelance writer and a client that fixes the pieces to be written, word counts, briefs, revisions, deadlines, payment and who owns the finished work. In India it is governed by the Indian Contract Act, 1872, and can be signed electronically.",
        ],
      },
      {
        heading: 'Which clauses does a content writing contract need?',
        body: [
          "Writing disputes usually involve shifting briefs, rewrites and who gets credit. These clauses settle them.",
        ],
        table: {
          head: ['Clause', 'What to write', 'Why it matters'],
          rows: [
            ['Deliverables', 'Number of pieces and approximate word count', 'Sets the size of the job'],
            ['Briefs', 'Topic and keywords agreed in writing before each piece', 'A changed brief is new work, not a revision'],
            ['Revisions', 'Rounds included per piece, and the rate after', 'Prevents unlimited rewrites'],
            ['Originality', 'Work is original and not copied', 'Clients need this for search and legal reasons'],
            ['AI use', 'Whether AI tools may be used, and how disclosed', 'Increasingly asked for by clients'],
            ['Byline', 'Ghostwritten, or credited to the writer', 'Avoids disputes over credit'],
            ['Copyright', 'Assigned to the client on full payment', 'The writer owns the work until assigned in writing'],
            ['Payment', 'Per piece or per word, with due dates', 'Clear basis for each invoice'],
          ],
        },
      },
      {
        heading: 'Who owns an article written by a freelancer?',
        body: [
          "The writer, until the copyright is assigned. An article is a literary work under the Copyright Act, 1957, and Section 17 makes the author the first owner. Ownership passes to the client through a written, signed assignment under Section 19.",
          "Even after assignment, Section 57 keeps the author's right to claim authorship and to object to distortion that harms their reputation. If the work is ghostwritten, say clearly that the client may publish it under its own name. See [who owns freelance work](/guides/who-owns-freelance-work-copyright-india).",
        ],
      },
      {
        heading: 'How do I write a content writing contract, step by step?',
        body: [
          "Settle these points before the first draft.",
        ],
        steps: [
          "Set the number of pieces, word counts and deadlines.",
          "Agree how briefs are given and confirmed.",
          "Fix the revision rounds and the rate for rewrites.",
          "State originality and any rules on AI tools.",
          "Agree the byline and publishing rights.",
          "Assign copyright on full payment, set rates and due dates, then sign.",
        ],
      },
      {
        heading: 'How should a content writer charge?',
        body: [
          "Charge per piece for predictable formats and per word for variable lengths, with a minimum per piece. For ongoing work, a monthly retainer for a fixed number of pieces, billed in advance, protects you from slow payers.",
          "Expect Indian business clients to deduct [TDS on professional fees](/guides/tds-on-freelancer-payments-india) once payments cross the annual threshold, and say in the contract that payment is net of any statutory deduction.",
        ],
      },
      {
        heading: 'What mistakes do writers make in contracts?',
        body: [
          "These are the most common gaps in writing agreements.",
        ],
        bullets: [
          "Accepting a changed topic as a “revision”.",
          "No limit on rewrites.",
          "No agreement on bylines or ghostwriting.",
          "Assigning copyright before payment.",
          "No rule on AI tools when the client cares about it.",
          "No minimum fee per piece for very short pieces.",
        ],
      },
      {
        heading: 'Where can I create a content writer contract online?',
        body: [
          "On Defellix you can start from a ready-made content writer template with the briefs, revisions, originality, byline and ownership clauses written in, then replace the bracketed details. Your client signs by email OTP, and every submission and approval is recorded with a timestamp. Your first contract is free.",
        ],
      },
    ],
    faq: [
      {
        q: 'Does the client own an article once they pay?',
        a: "Only if the contract assigns the copyright. Payment alone usually gives a right to use the article, so write the assignment into the contract and tie it to full payment.",
      },
      {
        q: 'Can I publish ghostwritten work in my portfolio?',
        a: "Only with the client's permission. Ghostwriting arrangements often keep the writer anonymous, so agree portfolio use in the contract.",
      },
      {
        q: 'Is a change of topic a revision?',
        a: "No. A revision improves the agreed piece. A new or changed topic is new work and should be quoted separately.",
      },
      {
        q: 'Should a writing contract mention AI tools?',
        a: "It helps to. Many clients now ask whether AI tools were used, so state what is allowed and how it will be disclosed.",
      },
    ],
  },
];

/**
 * Stable anchor id for a section heading, shared by the page and the
 * prerendered HTML so "#..." links resolve the same in both.
 */
export function guideSectionId(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

export function guideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}